import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useComposeEmail } from '../context/ComposeEmailContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { SalesTab, PhotographyTab, ProductionTab, AccountsTab, CommunicationsTab, ContactsTab, LogsTab } from '../components/organization';
import OrgAdvancedFilterDialog from '../components/organization/OrgAdvancedFilterDialog';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { DebouncedTextarea } from '../components/ui/debounced-textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Plus, 
  Search, 
  Filter,
  Building2, 
  Edit, 
  Trash2,
  MapPin,
  Phone,
  Mail,
  MoreVertical,
  Send,
  FolderOpen,
  FolderPlus,
  Users,
  User,
  Star,
  Eye,
  Clock,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Calendar,
  StickyNote,
  Save,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  PenLine,
  Package,
  Upload,
  Download,
  Building,
  PackageOpen,
  X,
  Plus as PlusIcon,
  History,
  Globe,
  Link,
  DollarSign,
  Camera,
  Factory,
  Receipt,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ShoppingCart,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate, formatTime } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const OrganizationsPage = () => {
  const { isBarbiePink } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Slide-in compose drawer — replaces the old "navigate to Email Hub" flow.
  const compose = useComposeEmail();
  const openCompose = compose.openCompose;
  const [organizations, setOrganizations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Ref to abort the previous fetchData call when filters change rapidly
  // (prevents stale responses from overwriting fresh ones, and avoids the
  // intermittent "Failed to load data" toast caused by race conditions
  // when typing in search or rapidly switching filters).
  const fetchAbortRef = React.useRef(null);
  const [sectorFilter, setSectorFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [availableRegions, setAvailableRegions] = useState([]);
  // Sort state — server-side. Default: photography_date_1 ASC.
  const [sortBy, setSortBy] = useState('photography_date_1');
  const [sortDir, setSortDir] = useState('asc');
  // Initial-load flag: when true, ask the backend to land on the page that
  // contains today's photography date. Cleared the first time data lands so
  // user pagination/filter changes don't re-trigger the auto-jump.
  const [autoPageMode, setAutoPageMode] = useState('today');
  // Advanced Filter state
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState(null); // { org_ids, year, filters, match_count } | null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingOrg, setViewingOrg] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState('overview');

  // Warn when the user switches to a different org while an unsent email
  // draft is still open in the slide-in drawer. We trigger a blocking
  // AlertDialog (via the context's `switchOrg` pendingAction) that asks the
  // operator to either FINISH the email or DISCARD the draft. The org view
  // has already switched — we don't revert that — but the draft is kept
  // intact and the drawer is auto-restored when "Finish email" is chosen.
  useEffect(() => {
    const currentId = viewingOrg?.org_id || null;
    if (!compose.open) return;
    if (!compose.originOrgId) return;
    if (compose.originOrgId === currentId) return;
    if (!compose.isDirty) return;
    compose.guardOrgSwitch(currentId);
    // Intentionally only depend on the org id so we don't fire while user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingOrg?.org_id]);

  const [quickWorkflowDialogOpen, setQuickWorkflowDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  
  // Orders tab state
  const [orderYear, setOrderYear] = useState(new Date().getFullYear());
  const [orderData, setOrderData] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newBuildingInstruction, setNewBuildingInstruction] = useState('');
  const [newPackingInstruction, setNewPackingInstruction] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docNotes, setDocNotes] = useState('');
  
  // Online Orders dialog state
  const [onlineOrdersOpen, setOnlineOrdersOpen] = useState(false);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [onlineOrdersLoading, setOnlineOrdersLoading] = useState(false);
  const [onlineOrdersSummary, setOnlineOrdersSummary] = useState({ total: 0, revenue: 0, familyOrders: 0 });
  
  // Refunds state
  const [refunds, setRefunds] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundsOpen, setRefundsOpen] = useState(false);
  const [editingRefund, setEditingRefund] = useState(null);
  const [newRefund, setNewRefund] = useState({
    order_no: '', child_name: '', class_team: '', total_purchased: '',
    reason: '', refund_amount: '', account_name: '', account_number: '',
    completed_by: '', refund_date: ''
  });
  const [addingRefund, setAddingRefund] = useState(false);
  
  // Building & Packing Instructions collapsible state
  const [buildingInstructionsOpen, setBuildingInstructionsOpen] = useState(false);
  const [packingInstructionsOpen, setPackingInstructionsOpen] = useState(false);
  const [orderNotesOpen, setOrderNotesOpen] = useState(true); // Order notes collapsible
  const [newOrderNote, setNewOrderNote] = useState(''); // New order note input
  
  // Document generation state
  const [docGenYear, setDocGenYear] = useState(new Date().getFullYear());
  const [docTemplate, setDocTemplate] = useState("preschool_confirmation");
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [documentTemplates, setDocumentTemplates] = useState([
    { id: "commission_claim", name: "Commission Claim Form" },
    { id: "preschool_confirmation", name: "Preschool Confirmation Letter" },
    { id: "primary_confirmation", name: "Primary School Confirmation Letter" },
    { id: "sport_confirmation", name: "Sport Club Confirmation Letter" }
  ]);
  
  // Workflow milestones collapsible state (tracks which workflows have expanded completed milestones)
  const [expandedWorkflowMilestones, setExpandedWorkflowMilestones] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    sector_id: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    phone: '',
    email: '',
    notes: '',
    online_url: '',
    online_only: false,
    website: '',
    region: ''
  });

  const [workflowData, setWorkflowData] = useState({
    name: '',
    workflow_type: 'school_photo', // Default type
    category: 'production',
    template_id: '',
    photography_date: '',
    milestones: []
  });

  // Email template selection dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailOrg, setSelectedEmailOrg] = useState(null);
  const [selectedEmailContact, setSelectedEmailContact] = useState(null);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);

  // Workflow confirmation email state
  const [showWorkflowEmailPrompt, setShowWorkflowEmailPrompt] = useState(false);
  const [pendingWorkflowOrg, setPendingWorkflowOrg] = useState(null);
  const [workflowEmailDialogOpen, setWorkflowEmailDialogOpen] = useState(false);

  // Contact management state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    mobile: '',
    role: '',
    notes_address: '',
    is_primary: false
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [deleteContactId, setDeleteContactId] = useState(null);

  // Notes editing state
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Logs tab state
  const [orgLogs, setOrgLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);

  // Communications tab state
  const [communications, setCommunications] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [newCommNote, setNewCommNote] = useState('');
  const [newCommType, setNewCommType] = useState('note');
  const [addingComm, setAddingComm] = useState(false);

  // Overview editing state
  const [editingOverviewField, setEditingOverviewField] = useState(null);
  const [overviewFieldValue, setOverviewFieldValue] = useState('');
  const [savingOverviewField, setSavingOverviewField] = useState(false);

  // Sales section state
  const [salesYear, setSalesYear] = useState(new Date().getFullYear());
  const [salesData, setSalesData] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesYears, setSalesYears] = useState([]);
  const [addingYear, setAddingYear] = useState(false);
  const [newYearValue, setNewYearValue] = useState('');
  const [salesDirty, setSalesDirty] = useState(false);
  const [savingSales, setSavingSales] = useState(false);
  const [originalSalesData, setOriginalSalesData] = useState({});

  // Unsaved-changes dialog state
  // pendingAction: { kind: 'tab' | 'year', value: any } — executed after user picks Save/Discard
  const [unsavedDialog, setUnsavedDialog] = useState({ open: false, pendingAction: null });

  // Orders collapsible state - removed, now using sub-tabs
  
  // Year-based sub-tab state
  const [activeYearTab, setActiveYearTab] = useState('sales'); // 'sales', 'photography', 'production', 'accounts', 'orders'

  const fetchData = useCallback(async () => {
    // Abort any in-flight previous fetch to prevent a slow stale response
    // from overwriting a newer one (causes flicker + the spurious
    // "Failed to load data" toast when an old request errors after a fresh
    // one has already succeeded).
    if (fetchAbortRef.current) {
      try { fetchAbortRef.current.abort(); } catch (_e) { /* noop */ }
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const signal = controller.signal;

    // Progress signal for the loading panel — gives the user visible feedback
    // on long fetches (large org lists / cross-org joins on backend).
    setLoadingStage('Connecting to server…');
    let progressTick = setTimeout(() => {
      if (!signal.aborted) setLoadingStage('Loading organisations…');
    }, 300);
    let progressTick2 = setTimeout(() => {
      if (!signal.aborted) setLoadingStage('This is taking longer than usual — still loading…');
    }, 4000);
    let progressTick3 = setTimeout(() => {
      if (!signal.aborted) setLoadingStage('Almost there — fetching sectors, regions and templates…');
    }, 8000);

    try {
      const params = new URLSearchParams();
      if (sectorFilter && sectorFilter !== 'all') params.append('sector_id', sectorFilter);
      if (regionFilter && regionFilter !== 'all') params.append('region', regionFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (advancedFilter?.org_ids) {
        // Explicit empty array means "no matches" — send sentinel so backend returns []
        params.append('org_ids', advancedFilter.org_ids.length > 0 ? advancedFilter.org_ids.join(',') : '__none__');
      }
      params.append('page', currentPage);
      params.append('limit', pageSize);
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);
      params.append('year', new Date().getFullYear());
      // First load: ask backend to land on the page containing today's
      // photography date. Only meaningful for the default ASC sort.
      if (autoPageMode === 'today' && sortBy === 'photography_date_1' && sortDir === 'asc') {
        params.append('auto_page', 'today');
      }

      // Use Promise.allSettled so a transient failure on a NON-CRITICAL
      // call (sectors / templates / regions) doesn't abort the whole batch
      // and falsely toast "Failed to load data" to the user.
      const [orgsResult, sectorsResult, templatesResult, regionsResult] = await Promise.allSettled([
        authFetch(`${API}/organizations?${params}`, { signal }),
        authFetch(`${API}/sectors`, { signal }),
        authFetch(`${API}/workflow-templates`, { signal }),
        authFetch(`${API}/organizations/regions`, { signal }),
      ]);

      // If this controller was aborted by a newer call, drop the response
      // silently (the newer call is already showing fresh data).
      if (signal.aborted) return;

      // CRITICAL: organizations call. Only this triggers the user-facing
      // toast. Other calls degrade gracefully.
      if (orgsResult.status === 'fulfilled' && orgsResult.value.ok) {
        setLoadingStage('Processing organisations…');
        const result = await orgsResult.value.json();
        setOrganizations(result.data || []);
        setTotalOrgs(result.total || 0);
        setTotalPages(result.total_pages || 1);
        // Backend may override `page` if auto_page=today landed us on a
        // different page — sync our local state so pagination buttons
        // reflect reality.
        if (typeof result.page === 'number' && result.page !== currentPage) {
          setCurrentPage(result.page);
        }
        // Clear the auto-page flag after the first successful load so any
        // subsequent fetch (filter/sort/page change) honours the user's
        // explicit page choice.
        if (autoPageMode === 'today') setAutoPageMode(null);
      } else if (orgsResult.status === 'rejected') {
        // Aborts shouldn't toast (they're expected during fast filter changes)
        const err = orgsResult.reason;
        if (err?.name !== 'AbortError') {
          console.error('Failed to fetch organizations:', err);
          toast.error('Failed to load organisations');
        }
      } else if (orgsResult.status === 'fulfilled' && !orgsResult.value.ok) {
        console.error(`Organizations fetch returned ${orgsResult.value.status}`);
        toast.error(`Failed to load organisations (HTTP ${orgsResult.value.status})`);
      }

      setLoadingStage('Finalising…');

      // Non-critical: log + skip on failure, never toast.
      if (sectorsResult.status === 'fulfilled' && sectorsResult.value.ok) {
        try { setSectors(await sectorsResult.value.json()); } catch (_e) { /* noop */ }
      } else if (sectorsResult.status === 'rejected' && sectorsResult.reason?.name !== 'AbortError') {
        console.warn('Sectors fetch failed (non-critical):', sectorsResult.reason);
      }

      if (templatesResult.status === 'fulfilled' && templatesResult.value.ok) {
        try { setWorkflowTemplates(await templatesResult.value.json()); } catch (_e) { /* noop */ }
      } else if (templatesResult.status === 'rejected' && templatesResult.reason?.name !== 'AbortError') {
        console.warn('Workflow templates fetch failed (non-critical):', templatesResult.reason);
      }

      if (regionsResult.status === 'fulfilled' && regionsResult.value.ok) {
        try {
          const regData = await regionsResult.value.json();
          setAvailableRegions(regData.regions || []);
        } catch (_e) { /* noop */ }
      } else if (regionsResult.status === 'rejected' && regionsResult.reason?.name !== 'AbortError') {
        console.warn('Regions fetch failed (non-critical):', regionsResult.reason);
      }
    } catch (err) {
      // This catch only fires if a synchronous error escapes — Promise.allSettled
      // never throws on its own.
      if (err?.name !== 'AbortError') {
        console.error('Unexpected error in fetchData:', err);
        toast.error('Failed to load data');
      }
    } finally {
      clearTimeout(progressTick);
      clearTimeout(progressTick2);
      clearTimeout(progressTick3);
      if (!signal.aborted) {
        setLoading(false);
        setLoadingStage('');
      }
    }
  }, [sectorFilter, regionFilter, debouncedSearch, currentPage, pageSize, advancedFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchData();
    fetchDocumentTemplates();
  }, [fetchData]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [sectorFilter, regionFilter, pageSize, advancedFilter]);

  // Toggle the active sort column. Clicking the active column flips the
  // direction; clicking a different column resets to ascending.
  const handleSortToggle = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  // Handle incoming navigation from Workflows page to select specific org
  useEffect(() => {
    const selectOrgId = location.state?.selectOrgId;
    if (selectOrgId) {
      // First check if org is already in loaded organizations
      const orgToSelect = organizations.find(o => o.org_id === selectOrgId);
      if (orgToSelect) {
        openViewDialog(orgToSelect);
        // Clear the state so it doesn't persist on refresh
        window.history.replaceState({}, document.title);
      } else if (!loading) {
        // Org not found in current list - fetch it directly
        const fetchOrgDirectly = async () => {
          try {
            const response = await authFetch(`${API}/organizations/${selectOrgId}`);
            if (response.ok) {
              const org = await response.json();
              openViewDialog(org);
            } else {
              toast.error('Organization not found');
            }
          } catch (err) {
            console.error('Error fetching org:', err);
          } finally {
            // Clear the state
            window.history.replaceState({}, document.title);
          }
        };
        fetchOrgDirectly();
      }
    }
  }, [location.state?.selectOrgId, organizations, loading]);

  // Handle URL query parameter ?org=<org_id> for navigation from Export Preview
  useEffect(() => {
    const orgId = searchParams.get('org');
    if (orgId && !loading) {
      // First check if org is already in loaded organizations
      const orgToSelect = organizations.find(o => o.org_id === orgId);
      if (orgToSelect) {
        openViewDialog(orgToSelect);
        // Clear the URL parameter
        setSearchParams({});
      } else {
        // Org not found in current list - fetch it directly
        const fetchOrgDirectly = async () => {
          try {
            const response = await authFetch(`${API}/organizations/${orgId}`);
            if (response.ok) {
              const org = await response.json();
              openViewDialog(org);
            } else {
              toast.error('Organization not found');
            }
          } catch (err) {
            console.error('Error fetching org:', err);
          } finally {
            // Clear the URL parameter
            setSearchParams({});
          }
        };
        fetchOrgDirectly();
      }
    }
  }, [searchParams, organizations, loading]);

  // AUTO-LOAD sales data when organization is opened
  useEffect(() => {
    if (viewingOrg && viewingOrg.org_id) {
      // Reset form ref to avoid stale data from previous org
      salesFormRef.current = {};
      setSalesDirty(false);
      // Fetch year-based data for the current year
      console.log('Auto-loading data for org:', viewingOrg.org_id, 'year:', salesYear);
      fetchSalesData(viewingOrg.org_id, salesYear);
      fetchOrderData(viewingOrg.org_id, orderYear);
      fetchRefunds(viewingOrg.org_id, salesYear);
      fetchCommunications(viewingOrg.org_id);
      fetchOrgLogs(viewingOrg.org_id, 1);
    }
  }, [viewingOrg?.org_id]); // Only trigger when org_id changes

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingOrg 
        ? `${API}/organizations/${editingOrg.org_id}`
        : `${API}/organizations`;
      
      const response = await authFetch(url, {
        method: editingOrg ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingOrg ? 'Organization updated' : 'Organization created');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        // 422 errors carry a Pydantic-shape `detail` array — flatten it so the
        // user actually sees which field failed instead of just "Failed to save".
        let detailMsg = errorData.detail;
        if (Array.isArray(detailMsg)) {
          detailMsg = detailMsg
            .map((e) => `${(e.loc || []).join('.')}: ${e.msg}`)
            .join('; ');
        }
        toast.error(detailMsg || `Failed to save organization (HTTP ${response.status})`);
        // Also log full payload + response for fast triage.
        // eslint-disable-next-line no-console
        console.error('Org save failed', { status: response.status, errorData, sentPayload: formData });
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Are you sure? This will also delete all contacts and workflows for this organization.')) return;
    
    try {
      const response = await authFetch(`${API}/organizations/${orgId}`, { method: 'DELETE' });

      if (response.ok) {
        toast.success('Organization deleted');
        fetchData();
      } else {
        toast.error('Failed to delete organization');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const openEditDialog = (org) => {
    setEditingOrg(org);
    // `notes` may be stored as an imported-PDF array (list of dicts) for
    // legacy orgs — never echo that back into the free-text textarea or the
    // PUT will hit a 422. Coerce non-string values to empty so the textarea
    // starts blank and only persists what the user actually types.
    const notesValue = typeof org.notes === 'string' ? org.notes : '';
    setFormData({
      name: org.name,
      sector_id: org.sector_id,
      address: org.address || '',
      city: org.city || '',
      state: org.state || '',
      postcode: org.postcode || '',
      phone: org.phone || '',
      email: org.email || '',
      notes: notesValue,
      online_url: org.online_url || '',
      online_only: org.online_only || false,
      website: org.website || '',
      region: org.region || ''
    });
    setDialogOpen(true);
  };

  const openViewDialog = async (org, tab = null) => {
    setViewLoading(true);
    setEditingNotes(false);
    // Sticky tabs: only override when caller explicitly passes a tab.
    // Otherwise keep whatever tab the user is currently viewing so
    // switching between orgs preserves context (e.g. Communications stays on Communications).
    const targetTab = tab || activeViewTab || 'overview';
    if (tab) {
      setActiveViewTab(tab);
    }
    try {
      const response = await authFetch(`${API}/organizations/${org.org_id}`);
      if (response.ok) {
        const fullOrg = await response.json();
        setViewingOrg(fullOrg);
        setNotesValue(fullOrg.notes || '');
        // Trigger data load for the active sticky tab on the new org
        if (targetTab === 'orders') fetchOrderData(fullOrg.org_id, orderYear);
        if (targetTab === 'logs') fetchOrgLogs(fullOrg.org_id, 1);
        if (targetTab === 'communications') fetchCommunications(fullOrg.org_id);
      }
    } catch (err) {
      toast.error('Failed to load organization details');
    } finally {
      setViewLoading(false);
    }
  };

  const saveNotes = async () => {
    if (!viewingOrg) return;
    setSavingNotes(true);
    try {
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ notes: notesValue })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Notes saved');
        setViewingOrg({ 
          ...viewingOrg, 
          notes: notesValue,
          notes_updated_at: data.notes_updated_at,
          notes_updated_by: data.notes_updated_by
        });
        setEditingNotes(false);
        fetchData(); // Refresh list
      } else {
        toast.error('Failed to save notes');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingNotes(false);
    }
  };

  const fetchOrgLogs = async (orgId, page = 1) => {
    setLogsLoading(true);
    try {
      const response = await authFetch(`${API}/organizations/${orgId}/logs?page=${page}&limit=20`);
      if (response.ok) {
        const data = await response.json();
        setOrgLogs(data.data || []);
        setLogsTotalPages(data.total_pages || 1);
        setLogsPage(page);
      }
    } catch (err) {
      console.error('Failed to fetch organization logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchCommunications = async (orgId) => {
    setCommunicationsLoading(true);
    try {
      const response = await authFetch(`${API}/organizations/${orgId}/communications`);
      if (response.ok) {
        const data = await response.json();
        setCommunications(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch communications:', err);
    } finally {
      setCommunicationsLoading(false);
    }
  };

  const addCommunicationNote = async () => {
    if (!viewingOrg || !newCommNote.trim()) return;
    setAddingComm(true);
    try {
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/communications`, {
        method: 'POST',
        body: JSON.stringify({ note_type: newCommType, content: newCommNote })
      });
      if (response.ok) {
        toast.success('Note added');
        setNewCommNote('');
        setNewCommType('note');
        fetchCommunications(viewingOrg.org_id);
      } else {
        toast.error('Failed to add note');
      }
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setAddingComm(false);
    }
  };

  const deleteCommunicationNote = async (noteId) => {
    if (!viewingOrg) return;
    try {
      const response = await authFetch(`${API}/organizations/${viewingOrg.org_id}/communications/${noteId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Note deleted');
        fetchCommunications(viewingOrg.org_id);
      }
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const moveImportedNote = async (noteId, targetOrgId) => {
    if (!viewingOrg || !noteId || !targetOrgId) return;
    if (targetOrgId === viewingOrg.org_id) {
      toast.error('Target organisation must be different from the source');
      return;
    }
    try {
      const response = await authJsonFetch(
        `${API}/organizations/${viewingOrg.org_id}/notes/${noteId}/move`,
        { method: 'POST', body: JSON.stringify({ target_org_id: targetOrgId }) }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Note moved to ${data.to_org_name}`);
        fetchCommunications(viewingOrg.org_id);
        return true;
      }
      const err = await response.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to move note');
      return false;
    } catch (err) {
      console.error('Move note error:', err);
      toast.error('An error occurred while moving the note');
      return false;
    }
  };

  const updateOverviewField = async (field, value) => {
    if (!viewingOrg) return;
    setSavingOverviewField(true);
    try {
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/field`, {
        method: 'PUT',
        body: JSON.stringify({ field, value: String(value), reason: '' })
      });

      if (response.ok) {
        const updated = await response.json();
        toast.success(`${field.replace('_', ' ')} updated`);
        setViewingOrg(updated);
        setEditingOverviewField(null);
        fetchData(); // Refresh list
        // Refresh logs
        fetchOrgLogs(viewingOrg.org_id, 1);
      } else {
        toast.error('Failed to update field');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingOverviewField(false);
    }
  };

  // Sales data functions
  const fetchSalesData = async (orgId, year) => {
    setSalesLoading(true);
    setSalesDirty(false);
    salesFormRef.current = {}; // Clear form ref to avoid stale data
    try {
      console.log('Fetching sales data for org:', orgId, 'year:', year);
      const [salesRes, yearsRes] = await Promise.all([
        authFetch(`${API}/organizations/${orgId}/sales/${year}`),
        authFetch(`${API}/organizations/${orgId}/sales-years`)
      ]);
      if (salesRes.ok) {
        const data = await salesRes.json();
        console.log('Sales data received:', data.sales_id || 'new record', 'fields:', Object.keys(data).length);
        setSalesData(data);
        setOriginalSalesData(data);
      }
      if (yearsRes.ok) {
        const data = await yearsRes.json();
        setSalesYears(data.years || []);
      }
    } catch (err) {
      console.error('Failed to fetch sales data:', err);
    } finally {
      setSalesLoading(false);
    }
  };

  // Ref to store form data without triggering re-renders
  const salesFormRef = useRef({});

  // Update form ref (no re-render) and mark dirty
  const updateSalesFormRef = useCallback((field, value) => {
    salesFormRef.current[field] = value;
    setSalesDirty(true);
  }, []);

  // Sync ref data to state when saving
  const saveSalesData = async () => {
    if (!viewingOrg) return;
    setSavingSales(true);
    try {
      // Merge ref data with current salesData
      const dataToSave = { ...salesData, ...salesFormRef.current };
      console.log('Saving sales data:', { org_id: viewingOrg.org_id, year: salesYear, fieldCount: Object.keys(dataToSave).length });
      
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/sales/${salesYear}/bulk`, {
        method: 'PUT',
        body: JSON.stringify({ data: dataToSave })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSalesData(updated);
        setOriginalSalesData(updated);
        salesFormRef.current = {}; // Clear the ref
        setSalesDirty(false);
        toast.success('Changes saved');
        console.log('Sales data saved successfully:', updated.sales_id);
      } else {
        const errorText = await response.text();
        console.error('Save failed:', response.status, errorText);
        toast.error(`Failed to save: ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to save sales data:', err);
      toast.error('Failed to save changes');
    } finally {
      setSavingSales(false);
    }
  };

  const addNewSalesYear = async () => {
    if (!viewingOrg || !newYearValue) return;
    const year = parseInt(newYearValue);
    if (isNaN(year) || year < 2020 || year > 2050) {
      toast.error('Invalid year');
      return;
    }
    try {
      const response = await authFetch(`${API}/organizations/${viewingOrg.org_id}/sales/${year}/add-year`, { method: 'POST' });
      if (response.ok) {
        toast.success(`Year ${year} added`);
        setSalesYear(year);
        fetchSalesData(viewingOrg.org_id, year);
        setAddingYear(false);
        setNewYearValue('');
      }
    } catch (err) {
      toast.error('Failed to add year');
    }
  };

  // ============================================================
  // Import Sales Data from Previous Year
  // ============================================================
  // Two-step flow driven by state below:
  //   1) `openImportPreview` — GET /import-preview → populate importPreview
  //      state, open the confirmation modal.
  //   2) `runImport(mode)` — POST /import-from-previous with the fields
  //      the operator ticked and their chosen overwrite/merge mode.
  //
  // Excluded fields (photography_date_1..5, date_booked,
  // confirmation_letter_sent) are enforced on the BACKEND regardless of
  // what we send; the UI just hides them so the operator can't tick them.
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importChecked, setImportChecked] = useState({}); // { field: true/false }
  const [importBusy, setImportBusy] = useState(false);

  const openImportPreview = async () => {
    if (!viewingOrg) return;
    try {
      const res = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/sales/${salesYear}/import-preview`
      );
      if (!res.ok) {
        toast.error('Could not load import preview');
        return;
      }
      const data = await res.json();
      if (!data.has_source) {
        toast.error(`No sales data found for ${data.source_year} to import from`);
        return;
      }
      setImportPreview(data);
      // Default: every offered field is ticked ON.
      const initial = {};
      (data.fields || []).forEach(f => { initial[f.field] = true; });
      setImportChecked(initial);
      setImportPreviewOpen(true);
    } catch (err) {
      toast.error('Failed to open import preview');
    }
  };

  const runImport = async (mode) => {
    if (!viewingOrg || !importPreview) return;
    const include_fields = Object.entries(importChecked)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (include_fields.length === 0) {
      toast.error('Select at least one field to import');
      return;
    }
    setImportBusy(true);
    try {
      const res = await authJsonFetch(
        `${API}/organizations/${viewingOrg.org_id}/sales/${salesYear}/import-from-previous`,
        {
          method: 'POST',
          body: JSON.stringify({ include_fields, mode }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Import failed');
        return;
      }
      const data = await res.json();
      toast.success(
        `Imported ${data.copied?.length || 0} field(s) from ${importPreview.source_year}` +
        (data.skipped_existing?.length ? ` · ${data.skipped_existing.length} skipped (already filled)` : '')
      );
      setImportPreviewOpen(false);
      setImportPreview(null);
      setImportChecked({});
      // Refresh the panel so the newly-imported values render immediately.
      fetchSalesData(viewingOrg.org_id, salesYear);
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setImportBusy(false);
    }
  };

  // Document generation functions
  const fetchDocumentTemplates = async () => {
    try {
      const response = await authFetch(`${API}/documents/templates`);
      if (response.ok) {
        const data = await response.json();
        setDocumentTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const fetchDocPreview = async (orgId, year) => {
    try {
      const response = await authFetch(`${API}/organizations/${orgId}/document-preview?year=${year}`);
      if (response.ok) {
        const data = await response.json();
        setDocPreview(data);
      }
    } catch (err) {
      console.error('Failed to fetch document preview:', err);
    }
  };

  const generateDocument = async () => {
    if (!viewingOrg) return;

    // 🔑 Popup-blocker bypass:
    // Browsers (Chrome / Safari) silently block `window.open(...)` calls
    // made AFTER an await — the user-gesture context is considered
    // consumed once the promise yields. We open the destination tab
    // SYNCHRONOUSLY here (still inside the click handler's call stack)
    // and redirect it once the API returns. This is the same pattern
    // Stripe Checkout uses. If the popup is blocked at this point we
    // fall back to an in-toast link the user can click manually.
    const placeholder = window.open('about:blank', '_blank');
    if (placeholder) {
      try {
        placeholder.document.write(
          '<title>Generating…</title>' +
            '<body style="font-family:system-ui,sans-serif;padding:2.5rem;color:#444;background:#fafafa;">' +
            '<h3 style="margin:0 0 8px;">Generating your document…</h3>' +
            '<p style="margin:0;color:#666;">This tab will redirect to Google Drive when ready.</p>' +
            '</body>'
        );
      } catch (_) {
        /* cross-origin write — harmless */
      }
    }

    setGeneratingDoc(true);

    const closePlaceholder = () => {
      if (placeholder && !placeholder.closed) placeholder.close();
    };

    try {
      const token = localStorage.getItem('gs_session_token');
      const response = await fetch(
        `${API}/organizations/${viewingOrg.org_id}/download-document?year=${docGenYear}&template=${docTemplate}`,
        {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          if (data.drive_link) {
            // Document saved to Google Drive — redirect the placeholder
            // tab we opened on click. If it was blocked, render an
            // inline "Open document" link in the toast so the user
            // can still get there with one click.
            if (placeholder && !placeholder.closed) {
              placeholder.location.href = data.drive_link;
              toast.success('Document saved to Google Drive!');
            } else {
              toast.success(
                <span>
                  Document saved.{' '}
                  <a
                    href={data.drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold"
                    data-testid="generate-document-open-fallback"
                  >
                    Open document ↗
                  </a>
                </span>,
                { duration: 20000 }
              );
            }
          } else if (data.direct_download && data.file_data) {
            // Direct download (Google Drive not connected) — placeholder
            // not needed.
            closePlaceholder();
            toast.success('Document generated! Downloading...');
            const byteCharacters = atob(data.file_data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: data.file_type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } else {
            closePlaceholder();
            toast.success('Document generated!');
          }
        } else {
          closePlaceholder();
          toast.error('Failed to generate document');
        }
      } else {
        closePlaceholder();
        const err = await response.json().catch(() => ({ detail: 'Download failed' }));
        console.error('Document generation error response:', err);
        toast.error(err.detail || 'Failed to generate document');
      }
    } catch (err) {
      closePlaceholder();
      console.error('Document generation error:', err);
      toast.error('Failed to generate document');
    } finally {
      setGeneratingDoc(false);
    }
  };

  // Fetch online orders for the organization
  const fetchOnlineOrders = async (orgName) => {
    console.log('fetchOnlineOrders called with:', orgName);
    if (!orgName) {
      console.log('No org name provided, returning');
      toast.error('No organization name available');
      return;
    }
    
    console.log('Opening dialog and fetching orders...');
    setOnlineOrdersLoading(true);
    setOnlineOrdersOpen(true);
    
    try {
      const url = `${API}/check-orders/org-orders?org_name=${encodeURIComponent(orgName)}`;
      console.log('Fetching from:', url);
      const response = await authFetch(url);
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Data received:', data);
        setOnlineOrders(data.orders || []);
        setOnlineOrdersSummary({
          total: data.total_orders || 0,
          revenue: data.total_revenue || 0,
          familyOrders: data.family_orders || 0
        });
      } else {
        console.error('Response not ok:', response.status);
        toast.error('Failed to fetch online orders');
        setOnlineOrders([]);
      }
    } catch (err) {
      console.error('Failed to fetch online orders:', err);
      toast.error('Failed to fetch online orders');
      setOnlineOrders([]);
    } finally {
      setOnlineOrdersLoading(false);
    }
  };


  const openEmailDialog = async (org) => {
    setSelectedEmailOrg(org);
    setEmailDialogOpen(true);
    setEmailTemplatesLoading(true);
    
    try {
      const response = await authFetch(`${API}/email-templates`);
      if (response.ok) {
        setEmailTemplates(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  const navigateToEmailHub = (org, template = null) => {
    // Renamed for clarity but kept the old name to minimise call-site
    // churn. Instead of navigating to /email-hub, this now opens the
    // global slide-in compose drawer with the same prefill payload —
    // mirrors the Data Preview flow so operators stay on the page.
    const targetContact = selectedEmailContact ||
      org.contacts?.find(c => c.is_primary) ||
      org.contacts?.[0];
    const primaryContact = org.contacts?.find(c => c.is_primary) || org.contacts?.[0];
    const orgName = org.name || '';
    const contactName = primaryContact?.name || targetContact?.name || 'there';

    // Substitute template variables client-side so the drawer shows the
    // resolved subject/body immediately.
    const replaceVars = (text) => {
      if (!text) return text;
      return String(text)
        .replace(/\{\{Org_name\}\}/gi, orgName || '')
        .replace(/\{\{Pri_contact\}\}/gi, contactName || '');
    };

    let subject = '';
    let body = '';
    let attachments = [];

    if (template) {
      subject = replaceVars(template.subject || '');
      body = replaceVars(template.body || '');
      if (orgName && !subject.toLowerCase().includes(orgName.toLowerCase())) {
        subject = `${orgName} - ${subject}`;
      }
      attachments = Array.isArray(template.attachments) ? template.attachments : [];
    } else {
      body = `Dear ${contactName},

I hope this email finds you well.

${orgName ? `I am reaching out regarding ${orgName}.` : ''}

[Your message here]

Best regards,
G.S Workflow Team`;
      subject = orgName ? `${orgName} - ` : '';
    }

    openCompose({
      to_email: targetContact?.email || org.email || '',
      to_name: targetContact?.name || '',
      org_id: org.org_id,
      org_name: orgName,
      contact_id: targetContact?.contact_id || '',
      subject,
      body,
      attachments,
      template_id: template?.template_id || null,
    });

    setEmailDialogOpen(false);
    setSelectedEmailContact(null);
  };

  // Contact management functions
  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      role: contact.role || '',
      notes_address: contact.notes_address || '',
      is_primary: contact.is_primary || false
    });
    setContactDialogOpen(true);
  };

  const openAddContact = () => {
    setEditingContact(null);
    setContactForm({
      name: '',
      email: '',
      phone: '',
      mobile: '',
      role: '',
      notes_address: '',
      is_primary: false
    });
    setContactDialogOpen(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!viewingOrg) return;
    
    setContactLoading(true);
    try {
      const url = editingContact 
        ? `${API}/contacts/${editingContact.contact_id}`
        : `${API}/contacts`;
      
      const body = {
        ...contactForm,
        org_id: viewingOrg.org_id
      };
      
      const response = await authJsonFetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(editingContact ? 'Contact updated' : 'Contact added');
        setContactDialogOpen(false);
        // Refresh org data
        openViewDialog(viewingOrg);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save contact');
      }
    } catch (err) {
      toast.error('Failed to save contact');
    } finally {
      setContactLoading(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteContactId) return;
    
    try {
      const response = await authFetch(`${API}/contacts/${deleteContactId}`, { method: 'DELETE' });

      if (response.ok) {
        toast.success('Contact deleted');
        setDeleteContactId(null);
        // Refresh org data
        if (viewingOrg) openViewDialog(viewingOrg);
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  const handleSetPrimary = async (contactId) => {
    if (!viewingOrg) return;
    
    try {
      const response = await authFetch(`${API}/organizations/${viewingOrg.org_id}/primary-contact/${contactId}`, { method: 'PUT' });

      if (response.ok) {
        toast.success('Primary contact updated');
        // Refresh org data
        openViewDialog(viewingOrg);
      } else {
        toast.error('Failed to set primary contact');
      }
    } catch (err) {
      toast.error('Failed to set primary contact');
    }
  };

  const openQuickWorkflow = async (org) => {
    setSelectedOrg(org);
    // Try to pre-populate Photography Date from sales_data for the current
    // year (field photography_date_1). Only prompts the user for a date if
    // that value isn't set on the org's current-year record.
    const currentYear = new Date().getFullYear();
    let prefillDate = '';
    try {
      const res = await authFetch(`${API}/organizations/${org.org_id}/sales/${currentYear}`);
      if (res.ok) {
        const sd = await res.json();
        const raw = sd?.photography_date_1;
        if (raw) {
          // Normalise to YYYY-MM-DD (strip any time component) so the
          // <Input type="date"> picker displays it correctly.
          prefillDate = String(raw).slice(0, 10);
        }
      }
    } catch (_e) {
      // Non-fatal — just fall back to an empty date (user picks manually)
    }
    setWorkflowData({
      name: `${org.name} - ${currentYear}`,
      workflow_type: 'school_photo',
      category: 'prephotoday',
      template_id: '',
      photography_date: prefillDate,
    });
    setQuickWorkflowDialogOpen(true);
  };

  const handleQuickWorkflow = async (e) => {
    e.preventDefault();
    // Show the confirmation email prompt
    setShowWorkflowEmailPrompt(true);
  };

  const createWorkflowOnly = async () => {
    await createWorkflowInternal(false);
  };

  const createWorkflowWithEmail = async () => {
    await createWorkflowInternal(true);
  };

  const createWorkflowInternal = async (sendConfirmationEmail) => {
    try {
      // Get template milestones if a template is selected
      let milestones = [];
      if (workflowData.template_id) {
        const template = workflowTemplates.find(t => t.template_id === workflowData.template_id);
        if (template) {
          milestones = (template.milestones || []).map((ms, idx) => ({
            ...ms,
            milestone_id: `ms_${Date.now()}_${idx}`,
            status: 'pending',
            order: idx
          }));
        }
      }
      
      // If photography date is set and no template milestones, auto-generate milestones
      if (workflowData.photography_date && milestones.length === 0) {
        milestones = [
          {
            milestone_id: `ms_${Date.now()}_1`,
            name: 'Confirmation Email',
            description: 'Send confirmation email to organisation',
            order: 1,
            due_days: -42,
            status: 'pending'
          },
          {
            milestone_id: `ms_${Date.now()}_2`,
            name: 'Photography Date',
            description: 'Photo session day',
            order: 2,
            due_days: 0,
            status: 'pending'
          },
          {
            milestone_id: `ms_${Date.now()}_3`,
            name: 'Dispatch Date',
            description: 'Dispatch photos to organisation',
            order: 3,
            due_days: 42,
            status: 'pending'
          }
        ];
      }
      
      const response = await authJsonFetch(`${API}/workflows`, {
        method: 'POST',
        body: JSON.stringify({
          ...workflowData,
          shoot_date: workflowData.photography_date, // For backend compatibility
          milestones,
          org_id: selectedOrg.org_id,
          year: new Date().getFullYear()
        })
      });

      if (response.ok) {
        toast.success('Workflow created');
        setQuickWorkflowDialogOpen(false);
        setShowWorkflowEmailPrompt(false);
        
        // Reset workflow data
        setWorkflowData({
          name: '',
          workflow_type: 'school_photo',
          category: 'production',
          template_id: '',
          photography_date: '',
          milestones: []
        });
        
        // Refresh org data and navigate to workflows tab if viewing
        if (viewingOrg && viewingOrg.org_id === selectedOrg.org_id) {
          await openViewDialog(selectedOrg, 'workflows');
        }

        // If user wants to send confirmation email, show the email template selector
        if (sendConfirmationEmail) {
          // Store the org for which we're sending the email
          setPendingWorkflowOrg(selectedOrg);
          // Load email templates if not already loaded
          if (emailTemplates.length === 0) {
            setEmailTemplatesLoading(true);
            try {
              const templatesRes = await authFetch(`${API}/email-templates`);
              if (templatesRes.ok) {
                const templatesData = await templatesRes.json();
                setEmailTemplates(templatesData);
              }
            } catch (err) {
              console.error('Failed to load templates:', err);
            } finally {
              setEmailTemplatesLoading(false);
            }
          }
          // Show the email template selection dialog
          setWorkflowEmailDialogOpen(true);
        }
      } else {
        toast.error('Failed to create workflow');
        setShowWorkflowEmailPrompt(false);
      }
    } catch (err) {
      toast.error('An error occurred');
      setShowWorkflowEmailPrompt(false);
    }
  };

  const calculateWorkflowProgress = (workflow) => {
    if (!workflow.milestones || workflow.milestones.length === 0) return 0;
    const completed = workflow.milestones.filter(m => m.status === 'completed').length;
    return Math.round((completed / workflow.milestones.length) * 100);
  };

  const getWorkflowStatusStyle = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      on_hold: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return styles[status] || styles.pending;
  };

  const resetForm = () => {
    setEditingOrg(null);
    setFormData({
      name: '',
      sector_id: '',
      address: '',
      city: '',
      state: '',
      postcode: '',
      phone: '',
      email: '',
      notes: '',
      online_url: '',
      online_only: false,
      website: '',
      region: ''
    });
  };

  const getSectorColor = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      gray: 'bg-gray-100 text-gray-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800'
    };
    return colors[sector?.color] || colors.gray;
  };

  const getSectorName = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    return sector?.display_name || 'Unknown';
  };

  // Sector Management Dialog State
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [sectorOrgCounts, setSectorOrgCounts] = useState({});
  const [editingSector, setEditingSector] = useState(null);
  const [sectorForm, setSectorForm] = useState({ name: '', display_name: '', color: 'blue' });
  const [sectorLoading, setSectorLoading] = useState(false);

  const handleSaveSector = async (e) => {
    e.preventDefault();
    setSectorLoading(true);
    try {
      const url = editingSector 
        ? `${API}/sectors/${editingSector.sector_id}`
        : `${API}/sectors`;
      const response = await authJsonFetch(url, {
        method: editingSector ? 'PUT' : 'POST',
        body: JSON.stringify(sectorForm)
      });
      if (response.ok) {
        toast.success(editingSector ? 'Sector updated' : 'Sector added');
        setSectorDialogOpen(false);
        setEditingSector(null);
        setSectorForm({ name: '', display_name: '', color: 'blue' });
        fetchData();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to save sector');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSectorLoading(false);
    }
  };

  const handleDeleteSector = async (sectorId) => {
    if (!window.confirm('Delete this sector?')) return;
    try {
      const response = await authFetch(`${API}/sectors/${sectorId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Sector deleted');
        fetchData();
      } else {
        toast.error('Failed to delete sector');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  // ==================== ORDER FUNCTIONS ====================
  
  const fetchOrderData = async (orgId, year) => {
    setOrderLoading(true);
    try {
      const response = await authFetch(`${API}/organizations/${orgId}/orders/${year}`);
      if (response.ok) {
        const data = await response.json();
        setOrderData(data);
      }
      // Also fetch refunds for the year
      fetchRefunds(orgId, year);
    } catch (err) {
      console.error('Failed to fetch order data:', err);
    } finally {
      setOrderLoading(false);
    }
  };

  const fetchRefunds = async (orgId, year) => {
    setRefundsLoading(true);
    try {
      const response = await authFetch(`${API}/organizations/${orgId}/refunds/${year}`);
      if (response.ok) {
        const data = await response.json();
        setRefunds(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch refunds:', err);
    } finally {
      setRefundsLoading(false);
    }
  };

  const handleAddRefund = async () => {
    if (!viewingOrg) return;
    setAddingRefund(true);
    try {
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/refunds`, {
        method: 'POST',
        body: JSON.stringify({ ...newRefund, year: salesYear })
      });
      if (response.ok) {
        toast.success('Refund added');
        setNewRefund({
          order_no: '', child_name: '', class_team: '', total_purchased: '',
          reason: '', refund_amount: '', account_name: '', account_number: '',
          completed_by: '', refund_date: ''
        });
        fetchRefunds(viewingOrg.org_id, salesYear);
      } else {
        toast.error('Failed to add refund');
      }
    } catch (err) {
      toast.error('Failed to add refund');
    } finally {
      setAddingRefund(false);
    }
  };

  const handleUpdateRefund = async (refundId, data) => {
    if (!viewingOrg) return;
    try {
      const response = await authJsonFetch(`${API}/organizations/${viewingOrg.org_id}/refunds/${refundId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      if (response.ok) {
        toast.success('Refund updated');
        setEditingRefund(null);
        fetchRefunds(viewingOrg.org_id, salesYear);
      }
    } catch (err) {
      toast.error('Failed to update refund');
    }
  };

  const handleDeleteRefund = async (refundId) => {
    if (!viewingOrg || !window.confirm('Delete this refund entry?')) return;
    try {
      const response = await authFetch(`${API}/organizations/${viewingOrg.org_id}/refunds/${refundId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Refund deleted');
        fetchRefunds(viewingOrg.org_id, salesYear);
      }
    } catch (err) {
      toast.error('Failed to delete refund');
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !viewingOrg) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('notes', docNotes);
    
    try {
      const response = await fetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/documents`,
        {
          method: 'POST',
          body: formData
        }
      );
      if (response.ok) {
        toast.success('Document uploaded');
        setDocNotes('');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        toast.error('Failed to upload document');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDeleteDoc = async (docId, docName) => {
    if (!viewingOrg) return;
    if (!window.confirm(`Are you sure you want to delete "${docName}"?`)) return;
    
    try {
      const response = await fetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/documents/${docId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Document deleted');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete document');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDoc = async (doc) => {
    if (!viewingOrg) return;
    
    try {
      toast.info('Downloading...');
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/documents/${doc.doc_id}/download`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = doc.original_filename || 'download';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
        toast.success('Download started');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to download document');
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed');
    }
  };

  const handleAddNote = async () => {
    if (!viewingOrg || !newNote.trim()) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newNote })
        }
      );
      if (response.ok) {
        toast.success('Note added');
        setNewNote('');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add note');
      }
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId, noteContent) => {
    if (!viewingOrg) return;
    const preview = noteContent.length > 50 ? noteContent.substring(0, 50) + '...' : noteContent;
    if (!window.confirm(`Are you sure you want to delete this note?\n\n"${preview}"`)) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/notes/${noteId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Note deleted');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete note');
      }
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const handleAddBuildingInstruction = async () => {
    if (!viewingOrg || !newBuildingInstruction.trim()) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/building-instructions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newBuildingInstruction })
        }
      );
      if (response.ok) {
        toast.success('Building instruction added');
        setNewBuildingInstruction('');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add instruction');
      }
    } catch (err) {
      toast.error('Failed to add instruction');
    }
  };

  const handleDeleteBuildingInstruction = async (instructionId, content) => {
    if (!viewingOrg) return;
    const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
    if (!window.confirm(`Are you sure you want to delete this building instruction?\n\n"${preview}"`)) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/building-instructions/${instructionId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Instruction deleted');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete instruction');
      }
    } catch (err) {
      toast.error('Failed to delete instruction');
    }
  };

  const handleAddPackingInstruction = async () => {
    if (!viewingOrg || !newPackingInstruction.trim()) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/packing-instructions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newPackingInstruction })
        }
      );
      if (response.ok) {
        toast.success('Packing instruction added');
        setNewPackingInstruction('');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add instruction');
      }
    } catch (err) {
      toast.error('Failed to add instruction');
    }
  };

  const handleDeletePackingInstruction = async (instructionId, content) => {
    if (!viewingOrg) return;
    const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
    if (!window.confirm(`Are you sure you want to delete this packing instruction?\n\n"${preview}"`)) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/packing-instructions/${instructionId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Instruction deleted');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete instruction');
      }
    } catch (err) {
      toast.error('Failed to delete instruction');
    }
  };

  // Order Notes handlers
  const handleAddOrderNote = async () => {
    if (!viewingOrg || !newOrderNote.trim()) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newOrderNote })
        }
      );
      if (response.ok) {
        toast.success('Note added');
        setNewOrderNote('');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add note');
      }
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteOrderNote = async (noteId, content) => {
    if (!viewingOrg) return;
    const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
    if (!window.confirm(`Are you sure you want to delete this note?\n\n"${preview}"`)) return;
    
    try {
      const response = await authFetch(
        `${API}/organizations/${viewingOrg.org_id}/orders/${orderYear}/notes/${noteId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Note deleted');
        fetchOrderData(viewingOrg.org_id, orderYear);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete note');
      }
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  // Generate year options (current year + next 10)
  const yearOptions = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + i);

  const sectorColors = ['blue', 'green', 'purple', 'gray', 'red', 'yellow'];

  return (
    <div data-testid="organizations-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-2">
              Organisations
              <Button
                data-testid="add-org-btn"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                size="icon"
                variant="ghost"
                className="rounded-full hover:bg-primary/10 hover:text-primary"
                title="Add Organisation"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage schools, sports clubs, and clients ({totalOrgs} total)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="goto-contacts-btn"
            variant="outline"
            onClick={() => navigate('/contacts')}
            className="gap-2"
          >
            <Users className="w-4 h-4" />
            Contacts
          </Button>
          <Button
            data-testid="goto-view-data-btn"
            variant="outline"
            onClick={() => navigate('/export-data')}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            View Data
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Search organisations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant={advancedFilter ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAdvancedFilterOpen(true)}
              data-testid="advanced-filter-btn"
              className="gap-2"
              title="Advanced multi-criteria, year-aware filter"
            >
              <Filter className="w-4 h-4" />
              Filter
              {advancedFilter && (
                <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">
                  {advancedFilter.match_count}
                </Badge>
              )}
            </Button>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger data-testid="sector-filter" className="w-full sm:w-40">
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(s => (
                  <SelectItem key={s.sector_id} value={s.sector_id}>{s.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger data-testid="region-filter" className="w-full sm:w-40">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {availableRegions.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="page-size-select" className="w-full sm:w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={async () => {
                setSectorDialogOpen(true);
                try {
                  const res = await authFetch(`${API}/sectors/counts`);
                  if (res.ok) {
                    const data = await res.json();
                    setSectorOrgCounts(data.counts || {});
                  }
                } catch (e) {}
              }}
              data-testid="manage-sectors-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Manage Sectors
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Advanced Filter chip */}
      {advancedFilter && (
        <Card className={cn(isBarbiePink && "card-brutalist")} data-testid="advanced-filter-active-chip">
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <Filter className="w-3 h-3" />
                Year {advancedFilter.year}
              </Badge>
              {advancedFilter.filters.map((f, i) => {
                const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                let pretty;
                if (f.op === 'month' && f.value) {
                  const m = parseInt(f.value, 10);
                  pretty = `${f.table}.${f.field} in ${monthNames[m-1] || f.value} ${advancedFilter.year}`;
                } else {
                  pretty = `${f.table}.${f.field} ${f.op}${f.value ? ` "${f.value}"` : ''}`;
                }
                return (
                  <Badge key={i} variant="outline" className="text-xs">
                    {pretty}
                  </Badge>
                );
              })}
              <span className="text-sm text-muted-foreground">
                → {advancedFilter.match_count} match{advancedFilter.match_count === 1 ? '' : 'es'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setAdvancedFilterOpen(true)} data-testid="advanced-filter-edit">
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAdvancedFilter(null)} data-testid="advanced-filter-clear">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <OrgAdvancedFilterDialog
        open={advancedFilterOpen}
        onOpenChange={setAdvancedFilterOpen}
        onApply={(result) => setAdvancedFilter(result)}
        currentYear={new Date().getFullYear()}
      />

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Panel - Organization List */}
        <div className={cn(
          "space-y-4",
          viewingOrg ? "lg:col-span-3" : "lg:col-span-12"
        )}>
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardContent className="p-0">
              <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={() => handleSortToggle('name')}
                          data-testid="org-sort-name"
                        >
                          Organization
                          {sortBy === 'name' && (
                            sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          )}
                        </button>
                      </TableHead>
                      {!viewingOrg && (
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => handleSortToggle('sector')}
                            data-testid="org-sort-sector"
                          >
                            Sector
                            {sortBy === 'sector' && (
                              sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            )}
                          </button>
                        </TableHead>
                      )}
                      {!viewingOrg && (
                        <TableHead className="hidden md:table-cell">
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => handleSortToggle('photography_date_1')}
                            data-testid="org-sort-photography-date"
                          >
                            Photography Date 1
                            {sortBy === 'photography_date_1' && (
                              sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            )}
                          </button>
                        </TableHead>
                      )}
                      {!viewingOrg && <TableHead className="hidden md:table-cell">Primary Contact</TableHead>}
                      {!viewingOrg && (
                        <TableHead className="hidden lg:table-cell">
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => handleSortToggle('city')}
                            data-testid="org-sort-city"
                          >
                            Location
                            {sortBy === 'city' && (
                              sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            )}
                          </button>
                        </TableHead>
                      )}
                      {!viewingOrg && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={viewingOrg ? 1 : 6} className="text-center py-8">
                          <div className="flex flex-col items-center gap-3" data-testid="org-list-loading">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            <span className="text-sm text-muted-foreground">
                              {loadingStage || 'Loading…'}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : organizations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={viewingOrg ? 1 : 6} className="text-center py-8 text-muted-foreground">
                          No organisations found
                        </TableCell>
                      </TableRow>
                    ) : (
                      organizations.map((org) => (
                        <TableRow 
                          key={org.org_id} 
                          data-testid={`org-row-${org.org_id}`}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50 transition-colors",
                            viewingOrg?.org_id === org.org_id && "bg-primary/10 border-l-4 border-l-primary"
                          )}
                          onClick={() => openViewDialog(org)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                "p-2 rounded-lg",
                                isBarbiePink ? "bg-pink-500/20" : "bg-blue-500/10"
                              )}>
                                <Building2 className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                {viewingOrg && (
                                  <Badge className={cn("text-xs", getSectorColor(org.sector_id))}>
                                    {getSectorName(org.sector_id)}
                                  </Badge>
                                )}
                                {!viewingOrg && org.email && (
                                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {org.email}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          {!viewingOrg && (
                            <TableCell>
                              <Badge className={getSectorColor(org.sector_id)}>
                                {getSectorName(org.sector_id)}
                              </Badge>
                            </TableCell>
                          )}
                          {!viewingOrg && (
                            <TableCell className="hidden md:table-cell" data-testid={`org-photo-date-${org.org_id}`}>
                              {org.photography_date_1 ? (
                                <span className="font-mono text-sm">{formatDate(org.photography_date_1)}</span>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                          )}
                          {!viewingOrg && (
                            <TableCell className="hidden md:table-cell">
                              {org.primary_contact ? (
                                <div className="flex items-center gap-2">
                                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                  <span>{org.primary_contact.name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">No primary contact</span>
                              )}
                            </TableCell>
                          )}
                          {!viewingOrg && (
                            <TableCell className="hidden lg:table-cell">
                              {org.city && (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <MapPin className="w-3 h-3" />
                                  {org.city}, {org.state}
                                </div>
                              )}
                            </TableCell>
                          )}
                          {!viewingOrg && (
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`org-actions-${org.org_id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openViewDialog(org)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openEditDialog(org)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => openEmailDialog(org)}>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Email
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openQuickWorkflow(org)}>
                                    <FolderOpen className="w-4 h-4 mr-2" />
                                    Start Workflow
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleDelete(org.org_id)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
              </Table>
            </div>
          </CardContent>
        
        {/* Pagination Controls */}
        {totalPages > 0 && (
          <div className="flex items-center justify-end px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-testid="first-page-btn"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-testid="last-page-btn"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
          </Card>
        </div>

        {/* Right Panel - Organization Details */}
        {viewingOrg && (
          <div className="lg:col-span-9">
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    {viewingOrg.name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(viewingOrg)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEmailDialog(viewingOrg)}>
                      <Send className="w-4 h-4 mr-1" />
                      Email
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openQuickWorkflow(viewingOrg)}>
                      <FolderOpen className="w-4 h-4 mr-1" />
                      Workflow
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setViewingOrg(null)} data-testid="close-org-details">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {viewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <Tabs value={activeViewTab} onValueChange={(tab) => {
                    setActiveViewTab(tab);
                    if (tab === 'orders' && viewingOrg) {
                      fetchOrderData(viewingOrg.org_id, orderYear);
                    }
                    if (tab === 'logs' && viewingOrg) {
                      fetchOrgLogs(viewingOrg.org_id, 1);
                    }
                    if (tab === 'communications' && viewingOrg) {
                      fetchCommunications(viewingOrg.org_id);
                    }
                  }}>
                    <TabsList className="flex-wrap">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="workflows">Workflows ({viewingOrg.workflows?.length || 0})</TabsTrigger>
                      <TabsTrigger value="communications">Communications</TabsTrigger>
                      <TabsTrigger value="contacts">Contacts ({viewingOrg.contacts?.length || 0})</TabsTrigger>
                      <TabsTrigger value="logs" className="gap-1">
                        <History className="w-3 h-3" />
                        Logs
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-6 mt-4">
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Sector</p>
                          <Badge className={getSectorColor(viewingOrg.sector_id)}>
                            {getSectorName(viewingOrg.sector_id)}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Year</p>
                          <p className="font-medium">{viewingOrg.year}</p>
                        </div>
                        {viewingOrg.city && (
                          <div>
                            <p className="text-sm text-muted-foreground">Location</p>
                            <p className="font-medium flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {viewingOrg.city}, {viewingOrg.state}
                            </p>
                          </div>
                        )}
                        {viewingOrg.phone && (
                          <div>
                            <p className="text-sm text-muted-foreground">Phone</p>
                            <p className="font-medium flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {viewingOrg.phone}
                            </p>
                          </div>
                        )}
                      </div>

                      {viewingOrg.email && (
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {viewingOrg.email}
                          </p>
                        </div>
                      )}

                      {(viewingOrg.address || viewingOrg.city || viewingOrg.postcode) && (
                        <div>
                          <p className="text-sm text-muted-foreground">Full Address</p>
                          <div className="font-medium space-y-0.5">
                            {viewingOrg.address && <p>{viewingOrg.address}</p>}
                            {(viewingOrg.city || viewingOrg.state || viewingOrg.postcode) && (
                              <p>
                                {[viewingOrg.city, viewingOrg.state, viewingOrg.postcode].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {viewingOrg.website && (
                        <div>
                          <p className="text-sm text-muted-foreground">Website</p>
                          <a 
                            href={viewingOrg.website.startsWith('http') ? viewingOrg.website : `https://${viewingOrg.website}`}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {viewingOrg.website}
                          </a>
                        </div>
                      )}

                      {viewingOrg.region && (
                        <div>
                          <p className="text-sm text-muted-foreground">Region</p>
                          <p className="font-medium">{viewingOrg.region}</p>
                        </div>
                      )}

                      {/* Primary Contact */}
                      {viewingOrg.primary_contact && (
                        <div className="p-4 border rounded-lg bg-muted/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-medium">Primary Contact</span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <p className="font-medium">{viewingOrg.primary_contact.name}</p>
                            {viewingOrg.primary_contact.role && (
                              <p className="text-muted-foreground">{viewingOrg.primary_contact.role}</p>
                            )}
                            {viewingOrg.primary_contact.email && (
                              <p className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {viewingOrg.primary_contact.email}
                              </p>
                            )}
                            {viewingOrg.primary_contact.phone && (
                              <p className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {viewingOrg.primary_contact.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Document Generation Card */}
                      <Card className="border-dashed border-2">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Generate Confirmation Letter
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4 space-y-4">
                          <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Template:</Label>
                              <Select 
                                value={docTemplate} 
                                onValueChange={(v) => setDocTemplate(v)}
                              >
                                <SelectTrigger className="w-64 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {documentTemplates.map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Year:</Label>
                              <Select 
                                value={String(docGenYear)} 
                                onValueChange={(v) => {
                                  setDocGenYear(Number(v));
                                  fetchDocPreview(viewingOrg.org_id, Number(v));
                                }}
                              >
                                <SelectTrigger className="w-24 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 3 + i).map(y => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchDocPreview(viewingOrg.org_id, docGenYear)}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Preview Data
                            </Button>
                            
                            <Button
                              size="sm"
                              onClick={() => generateDocument()}
                              disabled={generatingDoc}
                              className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
                            >
                              {generatingDoc ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              Generate & Save to Drive
                            </Button>
                          </div>
                          
                          {/* Preview Section */}
                          {docPreview && (
                            <div className="border rounded-lg p-4 bg-muted/30 space-y-4 text-sm">
                              <div className="font-semibold text-base border-b pb-2">Letter Preview - Data to be Merged</div>
                              
                              {/* Header Section */}
                              <div className="space-y-1 border-b pb-3">
                                <p className="font-medium">{docPreview.contact?.first_name} {docPreview.contact?.last_name}</p>
                                <p className="font-medium">{docPreview.organization?.name}</p>
                                <p>{docPreview.organization?.address || <span className="text-muted-foreground italic">No address</span>}</p>
                                <p>
                                  {[docPreview.organization?.city, docPreview.organization?.state, docPreview.organization?.postcode]
                                    .filter(Boolean).join(', ') || <span className="text-muted-foreground italic">No city/state</span>}
                                </p>
                              </div>
                              
                              {/* Greeting */}
                              <p>Dear {docPreview.contact?.first_name || <span className="text-muted-foreground italic">Contact</span>},</p>
                              
                              <p className="text-muted-foreground">Thank you for booking with GS Imaging Group for your {docGenYear} photographs...</p>
                              
                              {/* Details Table */}
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2 border rounded p-3 bg-background">
                                <div>
                                  <span className="text-muted-foreground">Customer Name:</span>
                                  <p className="font-medium">{docPreview.organization?.name || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Photography Date 1:</span>
                                  <p className="font-medium">{docPreview.sales_data?.photography_date_1 ? formatDate(docPreview.sales_data.photography_date_1) : <span className="text-amber-600">Not set</span>}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Photography Date 2:</span>
                                  <p className="font-medium">{docPreview.sales_data?.photography_date_2 ? formatDate(docPreview.sales_data.photography_date_2) : '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Start Time:</span>
                                  <p className="font-medium">{docPreview.sales_data?.photography_start_time || <span className="text-amber-600">Not set</span>}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Package Selected:</span>
                                  <p className="font-medium">{docPreview.sales_data?.package_selected || <span className="text-amber-600">Not set</span>}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Enrolled Students:</span>
                                  <p className="font-medium">{docPreview.sales_data?.number_of_enrolled_students || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Group Type:</span>
                                  <p className="font-medium">{docPreview.sales_data?.group_type || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Staff Photos:</span>
                                  <p className="font-medium">{docPreview.sales_data?.staff_photos || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Graduation Photos:</span>
                                  <p className="font-medium">{docPreview.sales_data?.graduation || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Payment System:</span>
                                  <p className="font-medium">{docPreview.sales_data?.payment_system || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Vehicle Access:</span>
                                  <p className="font-medium">{docPreview.sales_data?.vehicle_access || '-'}</p>
                                </div>
                              </div>
                              
                              {!docPreview.has_sales_data && (
                                <div className="flex items-center gap-2 text-amber-600 text-xs">
                                  <AlertTriangle className="w-3 h-3" />
                                  No sales data found for {docGenYear}. Fill in the Sales tab first.
                                </div>
                              )}
                            </div>
                          )}
                          
                          <p className="text-xs text-muted-foreground">
                            Click "Preview Data" to see what will be merged into the letter before downloading.
                          </p>
                        </CardContent>
                      </Card>

                      {/* Year-Based Data Section */}
                      <div className="border rounded-lg p-4">
                        {/* Year Selector */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Label className="font-medium">Year:</Label>
                            <Select value={String(salesYear)} onValueChange={(v) => {
                              // Warn if changing year with unsaved changes — use in-app dialog
                              if (salesDirty) {
                                setUnsavedDialog({ open: true, pendingAction: { kind: 'year', value: Number(v) } });
                                return;
                              }
                              const year = Number(v);
                              setSalesYear(year);
                              setOrderYear(year);
                              if (viewingOrg) {
                                if (['sales', 'photography', 'production', 'accounts'].includes(activeYearTab)) {
                                  fetchSalesData(viewingOrg.org_id, year);
                                } else if (activeYearTab === 'orders') {
                                  fetchOrderData(viewingOrg.org_id, year);
                                }
                              }
                            }}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                                {salesYears.filter(y => ![2024, 2025, 2026, 2027, 2028, 2029, 2030].includes(y)).map(y => (
                                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {!addingYear ? (
                              <>
                                <Button variant="outline" size="sm" onClick={() => setAddingYear(true)}>
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Year
                                </Button>
                                {/* Import Data — copies non-year-specific
                                    sales fields from the immediately-previous
                                    year (excludes photography dates 1-5,
                                    date_booked, confirmation_letter_sent).
                                    Opens a preview modal so the operator can
                                    tick individual fields before committing. */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={openImportPreview}
                                  data-testid="import-previous-year-btn"
                                  title={`Copy sales data from ${salesYear - 1} into ${salesYear}`}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  Import Data
                                </Button>
                              </>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Input
                                  className="w-24 h-8"
                                  placeholder="Year"
                                  value={newYearValue}
                                  onChange={(e) => setNewYearValue(e.target.value)}
                                  type="number"
                                />
                                <Button size="sm" className="h-8" onClick={addNewSalesYear}>Add</Button>
                                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAddingYear(false); setNewYearValue(''); }}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Sub-tabs for Sales, Photography, Production, Accounts, and Orders */}
                        <Tabs value={activeYearTab} onValueChange={(tab) => {
                          // Warn if switching away with unsaved changes — use in-app dialog
                          // (window.confirm's Cancel is unreliable in some browsers/extensions).
                          if (salesDirty && ['sales', 'photography', 'production', 'accounts'].includes(activeYearTab)) {
                            setUnsavedDialog({ open: true, pendingAction: { kind: 'tab', value: tab } });
                            return;
                          }
                          setActiveYearTab(tab);
                          if (viewingOrg) {
                            if (['sales', 'photography', 'production', 'accounts'].includes(tab)) {
                              fetchSalesData(viewingOrg.org_id, salesYear);
                            } else if (tab === 'orders') {
                              fetchOrderData(viewingOrg.org_id, salesYear);
                            }
                          }
                        }}>
                          <TabsList className="w-full grid grid-cols-5">
                            <TabsTrigger value="sales" className="gap-1">
                              <DollarSign className="w-3 h-3" />
                              Sales
                            </TabsTrigger>
                            <TabsTrigger value="photography" className="gap-1">
                              <Camera className="w-3 h-3" />
                              Photo
                            </TabsTrigger>
                            <TabsTrigger value="production" className="gap-1">
                              <Factory className="w-3 h-3" />
                              Prod
                            </TabsTrigger>
                            <TabsTrigger value="accounts" className="gap-1">
                              <Receipt className="w-3 h-3" />
                              Accts
                            </TabsTrigger>
                            <TabsTrigger value="orders" className="gap-1">
                              <Package className="w-3 h-3" />
                              Orders
                            </TabsTrigger>
                          </TabsList>

                          {/* Sales Tab */}
                          <TabsContent value="sales" className="mt-4">
                            <SalesTab
                              salesYear={salesYear}
                              salesData={salesData}
                              salesLoading={salesLoading}
                              salesDirty={salesDirty}
                              savingSales={savingSales}
                              saveSalesData={saveSalesData}
                              updateSalesFormRef={updateSalesFormRef}
                            />
                          </TabsContent>

                          {/* Photography Tab */}
                          <TabsContent value="photography" className="mt-4">
                            <PhotographyTab
                              salesYear={salesYear}
                              salesData={salesData}
                              salesLoading={salesLoading}
                              salesDirty={salesDirty}
                              savingSales={savingSales}
                              saveSalesData={saveSalesData}
                              updateSalesFormRef={updateSalesFormRef}
                            />
                          </TabsContent>

                          {/* Production Tab */}
                          <TabsContent value="production" className="mt-4">
                            <ProductionTab
                              salesYear={salesYear}
                              salesData={salesData}
                              salesLoading={salesLoading}
                              salesDirty={salesDirty}
                              savingSales={savingSales}
                              saveSalesData={saveSalesData}
                              updateSalesFormRef={updateSalesFormRef}
                            />
                          </TabsContent>

                          {/* Accounts Tab */}
                          <TabsContent value="accounts" className="mt-4">
                            <AccountsTab
                              salesYear={salesYear}
                              salesData={salesData}
                              salesLoading={salesLoading}
                              salesDirty={salesDirty}
                              savingSales={savingSales}
                              saveSalesData={saveSalesData}
                              updateSalesFormRef={updateSalesFormRef}
                            />
                          </TabsContent>

                          {/* Orders Tab */}
                          <TabsContent value="orders" className="mt-4">
                            {orderLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin" />
                              </div>
                            ) : (
                              <div className="space-y-6">
                                {/* Online Orders Button — fetched from local woo_orders, matched on product description containing the org name */}
                                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                                  <div>
                                    <h4 className="font-medium text-blue-900 dark:text-blue-100">Online Orders</h4>
                                    <p className="text-sm text-blue-700 dark:text-blue-300">Every order whose product description contains "{viewingOrg?.name}"</p>
                                  </div>
                                  <Button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      fetchOnlineOrders(viewingOrg?.name);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid="online-orders-btn"
                                  >
                                    <ShoppingCart className="w-4 h-4 mr-2" />
                                    View Online Orders
                                  </Button>
                                </div>

                                {/* Documents Section */}
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-medium flex items-center gap-2">
                                      <FileText className="w-4 h-4" />
                                      Documents ({orderData?.documents?.length || 0})
                                    </h4>
                                    <Button 
                                      size="sm" 
                                      onClick={() => document.getElementById('doc-upload-year')?.click()}
                                      disabled={uploadingDoc}
                                    >
                                      {uploadingDoc ? (
                                        <>
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          Uploading...
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-3 h-3 mr-1" />
                                          Upload
                                        </>
                                      )}
                                    </Button>
                                    <input
                                      id="doc-upload-year"
                                      type="file"
                                      className="hidden"
                                      onChange={handleDocUpload}
                                    />
                                  </div>
                                  {uploadingDoc && (
                                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="text-sm font-medium">Uploading document...</span>
                                      </div>
                                      <div className="mt-2 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{width: '60%'}} />
                                      </div>
                                    </div>
                                  )}
                                  {orderData?.documents?.length > 0 ? (
                                    <div className="space-y-2">
                                      {orderData.documents.map(doc => (
                                        <div key={doc.doc_id} className="p-3 border rounded-lg flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-muted-foreground" />
                                            <div>
                                              <p className="font-medium text-sm">{doc.original_filename}</p>
                                              <p className="text-xs text-muted-foreground">
                                                {(doc.file_size / 1024).toFixed(1)} KB • {formatDate(doc.uploaded_at)}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" onClick={() => handleDownloadDoc(doc)}>
                                              <Download className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.doc_id, doc.original_filename)}>
                                              <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">No documents uploaded for {salesYear}</p>
                                  )}
                                </div>

                                {/* Order Notes Section - Collapsible */}
                                <Collapsible open={orderNotesOpen} onOpenChange={setOrderNotesOpen}>
                                  <div className="border rounded-lg">
                                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                                      <div className="flex items-center gap-2">
                                        <StickyNote className="w-4 h-4" />
                                        <span className="font-medium">Order Notes ({orderData?.notes?.length || 0})</span>
                                      </div>
                                      <ChevronDown className={cn("w-4 h-4 transition-transform", orderNotesOpen && "rotate-180")} />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="px-4 pb-4 space-y-3 border-t">
                                        <div className="flex gap-2 pt-3">
                                          <Textarea
                                            value={newOrderNote}
                                            onChange={(e) => setNewOrderNote(e.target.value)}
                                            placeholder="Add a note..."
                                            rows={2}
                                            className="flex-1"
                                          />
                                          <Button onClick={handleAddOrderNote} disabled={!newOrderNote.trim()} size="icon">
                                            <Plus className="w-4 h-4" />
                                          </Button>
                                        </div>
                                        {orderData?.notes?.length > 0 && (
                                          <div className="space-y-2">
                                            {[...orderData.notes]
                                              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                              .map((note) => (
                                              <div key={note.note_id} className="p-3 border rounded-lg bg-muted/30">
                                                <div className="flex items-start justify-between gap-2">
                                                  <p className="text-sm flex-1 whitespace-pre-wrap">{note.content}</p>
                                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteOrderNote(note.note_id, note.content)}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                  </Button>
                                                </div>
                                                <div className="mt-2 pt-2 border-t border-muted flex items-center gap-2 text-xs text-muted-foreground">
                                                  <User className="w-3 h-3" />
                                                  <span>{note.created_by_name || 'Unknown'}</span>
                                                  <span>•</span>
                                                  <Clock className="w-3 h-3" />
                                                  <span>{formatDate(note.created_at)}</span>
                                                  {note.updated_at && note.updated_at !== note.created_at && (
                                                    <>
                                                      <span>•</span>
                                                      <span className="italic">edited by {note.updated_by_name || 'Unknown'}</span>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>

                                {/* Building Instructions - Collapsible */}
                                <Collapsible open={buildingInstructionsOpen} onOpenChange={setBuildingInstructionsOpen}>
                                  <div className="border rounded-lg">
                                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                                      <div className="flex items-center gap-2">
                                        <Building className="w-4 h-4" />
                                        <span className="font-medium">Building Instructions ({orderData?.building_instructions?.length || 0})</span>
                                      </div>
                                      <ChevronDown className={cn("w-4 h-4 transition-transform", buildingInstructionsOpen && "rotate-180")} />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="px-4 pb-4 space-y-3 border-t">
                                        {orderData?.building_instructions?.length > 0 && (
                                          <div className="space-y-2 pt-3">
                                            {orderData.building_instructions.map((inst) => (
                                              <div key={inst.instruction_id} className="p-3 border rounded-lg flex items-start justify-between gap-2">
                                                <p className="text-sm flex-1 whitespace-pre-wrap">{inst.content}</p>
                                                <Button variant="ghost" size="icon" onClick={() => handleDeleteBuildingInstruction(inst.instruction_id, inst.content)}>
                                                  <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div className="flex gap-2 pt-2">
                                          <Textarea
                                            value={newBuildingInstruction}
                                            onChange={(e) => setNewBuildingInstruction(e.target.value)}
                                            placeholder="Add building instruction..."
                                            rows={2}
                                            className="flex-1"
                                          />
                                          <Button onClick={handleAddBuildingInstruction} disabled={!newBuildingInstruction.trim()} size="icon">
                                            <Plus className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>

                                {/* Packing Instructions - Collapsible */}
                                <Collapsible open={packingInstructionsOpen} onOpenChange={setPackingInstructionsOpen}>
                                  <div className="border rounded-lg">
                                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                                      <div className="flex items-center gap-2">
                                        <PackageOpen className="w-4 h-4" />
                                        <span className="font-medium">Packing Instructions ({orderData?.packing_instructions?.length || 0})</span>
                                      </div>
                                      <ChevronDown className={cn("w-4 h-4 transition-transform", packingInstructionsOpen && "rotate-180")} />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="px-4 pb-4 space-y-3 border-t">
                                        {orderData?.packing_instructions?.length > 0 && (
                                          <div className="space-y-2 pt-3">
                                            {orderData.packing_instructions.map((inst) => (
                                              <div key={inst.instruction_id} className="p-3 border rounded-lg flex items-start justify-between gap-2">
                                                <p className="text-sm flex-1 whitespace-pre-wrap">{inst.content}</p>
                                                <Button variant="ghost" size="icon" onClick={() => handleDeletePackingInstruction(inst.instruction_id, inst.content)}>
                                                  <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div className="flex gap-2 pt-2">
                                          <Textarea
                                            value={newPackingInstruction}
                                            onChange={(e) => setNewPackingInstruction(e.target.value)}
                                            placeholder="Add packing instruction..."
                                            rows={2}
                                            className="flex-1"
                                          />
                                          <Button onClick={handleAddPackingInstruction} disabled={!newPackingInstruction.trim()} size="icon">
                                            <Plus className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>

                                {/* Refunds Section - Collapsible */}
                                <Collapsible open={refundsOpen} onOpenChange={setRefundsOpen}>
                                  <div className="border rounded-lg">
                                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                                      <div className="flex items-center gap-2">
                                        <Receipt className="w-4 h-4" />
                                        <span className="font-medium">Refunds ({refunds.length})</span>
                                      </div>
                                      <ChevronDown className={cn("w-4 h-4 transition-transform", refundsOpen && "rotate-180")} />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="px-4 pb-4 space-y-4 border-t">
                                        {/* Add New Refund Form */}
                                        <div className="pt-4 space-y-3" data-testid="add-refund-form">
                                          <h5 className="text-sm font-medium">Add New Refund</h5>
                                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                            <Input
                                              placeholder="Order No."
                                              value={newRefund.order_no}
                                              onChange={(e) => setNewRefund({...newRefund, order_no: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-order-no"
                                            />
                                            <Input
                                              placeholder="Child Name"
                                              value={newRefund.child_name}
                                              onChange={(e) => setNewRefund({...newRefund, child_name: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-child-name"
                                            />
                                            <Input
                                              placeholder="Class/Team"
                                              value={newRefund.class_team}
                                              onChange={(e) => setNewRefund({...newRefund, class_team: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-class-team"
                                            />
                                            <Input
                                              placeholder="Total Purchased"
                                              value={newRefund.total_purchased}
                                              onChange={(e) => setNewRefund({...newRefund, total_purchased: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-total-purchased"
                                            />
                                            <Input
                                              placeholder="Reason"
                                              value={newRefund.reason}
                                              onChange={(e) => setNewRefund({...newRefund, reason: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-reason"
                                            />
                                            <Input
                                              placeholder="Refund Amount"
                                              value={newRefund.refund_amount}
                                              onChange={(e) => setNewRefund({...newRefund, refund_amount: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-amount"
                                            />
                                            <Input
                                              placeholder="Account Name"
                                              value={newRefund.account_name}
                                              onChange={(e) => setNewRefund({...newRefund, account_name: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-account-name"
                                            />
                                            <Input
                                              placeholder="Account Number"
                                              value={newRefund.account_number}
                                              onChange={(e) => setNewRefund({...newRefund, account_number: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-account-number"
                                            />
                                            <Input
                                              placeholder="Completed By"
                                              value={newRefund.completed_by}
                                              onChange={(e) => setNewRefund({...newRefund, completed_by: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-completed-by"
                                            />
                                            <Input
                                              type="date"
                                              placeholder="Date of Refund"
                                              value={newRefund.refund_date}
                                              onChange={(e) => setNewRefund({...newRefund, refund_date: e.target.value})}
                                              className="h-8 text-sm"
                                              data-testid="refund-date"
                                            />
                                          </div>
                                          <Button size="sm" onClick={handleAddRefund} disabled={addingRefund} data-testid="add-refund-btn">
                                            {addingRefund ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                                            Add Refund
                                          </Button>
                                        </div>

                                        {/* Refunds Table */}
                                        {refundsLoading ? (
                                          <div className="flex items-center justify-center py-4">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                          </div>
                                        ) : refunds.length > 0 ? (
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm border-collapse">
                                              <thead>
                                                <tr className="bg-muted/50">
                                                  <th className="p-2 text-left font-medium border">Order No.</th>
                                                  <th className="p-2 text-left font-medium border">Child Name</th>
                                                  <th className="p-2 text-left font-medium border">Class/Team</th>
                                                  <th className="p-2 text-left font-medium border">Total</th>
                                                  <th className="p-2 text-left font-medium border">Reason</th>
                                                  <th className="p-2 text-left font-medium border">Refund Amt</th>
                                                  <th className="p-2 text-left font-medium border">Acc Name</th>
                                                  <th className="p-2 text-left font-medium border">Acc No.</th>
                                                  <th className="p-2 text-left font-medium border">Completed By</th>
                                                  <th className="p-2 text-left font-medium border">Date</th>
                                                  <th className="p-2 text-center font-medium border w-20">Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {refunds.map(refund => (
                                                  editingRefund?.refund_id === refund.refund_id ? (
                                                    <tr key={refund.refund_id} className="bg-blue-50/50 dark:bg-blue-950/30">
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.order_no || ''} onChange={(e) => setEditingRefund({...editingRefund, order_no: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.child_name || ''} onChange={(e) => setEditingRefund({...editingRefund, child_name: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.class_team || ''} onChange={(e) => setEditingRefund({...editingRefund, class_team: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.total_purchased || ''} onChange={(e) => setEditingRefund({...editingRefund, total_purchased: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.reason || ''} onChange={(e) => setEditingRefund({...editingRefund, reason: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.refund_amount || ''} onChange={(e) => setEditingRefund({...editingRefund, refund_amount: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.account_name || ''} onChange={(e) => setEditingRefund({...editingRefund, account_name: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.account_number || ''} onChange={(e) => setEditingRefund({...editingRefund, account_number: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input className="h-7 text-sm" value={editingRefund.completed_by || ''} onChange={(e) => setEditingRefund({...editingRefund, completed_by: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border">
                                                        <Input type="date" className="h-7 text-sm" value={editingRefund.refund_date || ''} onChange={(e) => setEditingRefund({...editingRefund, refund_date: e.target.value})} />
                                                      </td>
                                                      <td className="p-1 border text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleUpdateRefund(editingRefund.refund_id, editingRefund)}>
                                                            <Save className="w-3 h-3 text-green-600" />
                                                          </Button>
                                                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingRefund(null)}>
                                                            <X className="w-3 h-3 text-muted-foreground" />
                                                          </Button>
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  ) : (
                                                    <tr key={refund.refund_id} className="hover:bg-muted/30">
                                                      <td className="p-2 border">{refund.order_no || '-'}</td>
                                                      <td className="p-2 border">{refund.child_name || '-'}</td>
                                                      <td className="p-2 border">{refund.class_team || '-'}</td>
                                                      <td className="p-2 border">{refund.total_purchased || '-'}</td>
                                                      <td className="p-2 border">{refund.reason || '-'}</td>
                                                      <td className="p-2 border font-medium">{refund.refund_amount || '-'}</td>
                                                      <td className="p-2 border">{refund.account_name || '-'}</td>
                                                      <td className="p-2 border">{refund.account_number || '-'}</td>
                                                      <td className="p-2 border">{refund.completed_by || '-'}</td>
                                                      <td className="p-2 border">{refund.refund_date ? formatDate(refund.refund_date) : '-'}</td>
                                                      <td className="p-2 border text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingRefund({...refund})} data-testid={`edit-refund-${refund.refund_id}`}>
                                                            <Edit className="w-3 h-3 text-blue-600" />
                                                          </Button>
                                                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDeleteRefund(refund.refund_id)} data-testid={`delete-refund-${refund.refund_id}`}>
                                                            <Trash2 className="w-3 h-3 text-destructive" />
                                                          </Button>
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  )
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-muted-foreground text-center py-4">No refunds recorded for {salesYear}</p>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>
                      </div>
                    </TabsContent>

                    {/* Workflows Tab */}
                    <TabsContent value="workflows" className="mt-4">
                      {viewingOrg.workflows?.length > 0 ? (
                        <div className="space-y-4 max-h-[500px] overflow-y-auto">
                          {viewingOrg.workflows.map(wf => {
                            const photoDate = wf.photography_date || wf.shoot_date;
                            const milestones = wf.milestones || [];
                            
                            // Find Photography Date milestone index (due_days === 0 or name contains "Photography")
                            const photoMilestoneIndex = milestones.findIndex(m => 
                              m.due_days === 0 || m.name?.toLowerCase().includes('photography')
                            );
                            
                            // Split milestones into before photo day (completed can be collapsed) and after
                            const beforePhotoMilestones = photoMilestoneIndex > 0 
                              ? milestones.slice(0, photoMilestoneIndex) 
                              : [];
                            const photoMilestone = photoMilestoneIndex >= 0 ? milestones[photoMilestoneIndex] : null;
                            const afterPhotoMilestones = photoMilestoneIndex >= 0 
                              ? milestones.slice(photoMilestoneIndex + 1)
                              : milestones;
                            
                            // Count completed before photo date
                            const completedBeforeCount = beforePhotoMilestones.filter(m => m.status === 'completed').length;
                            const pendingBeforeCount = beforePhotoMilestones.length - completedBeforeCount;
                            const isExpanded = expandedWorkflowMilestones[wf.workflow_id] ?? false;
                            
                            // Helper to render a milestone on timeline
                            const renderMilestone = (ms, idx, isPhotoDay = false) => {
                              let dueDateStr = null;
                              if (photoDate && ms.due_days !== undefined) {
                                const baseDate = new Date(photoDate);
                                const dueDate = new Date(baseDate);
                                dueDate.setDate(dueDate.getDate() + ms.due_days);
                                dueDateStr = dueDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' });
                              }
                              const isCompleted = ms.status === 'completed';
                              const isInProgress = ms.status === 'in_progress';
                              
                              return (
                                <div key={ms.milestone_id || idx} className="flex items-start gap-3">
                                  {/* Timeline dot and line */}
                                  <div className="flex flex-col items-center">
                                    <div className={cn(
                                      "w-3 h-3 rounded-full shrink-0 mt-0.5",
                                      isPhotoDay ? "bg-blue-500 ring-2 ring-blue-200" :
                                      isCompleted ? "bg-green-500" :
                                      isInProgress ? "bg-blue-500" :
                                      "border-2 border-gray-300 bg-white"
                                    )} />
                                    <div className="w-0.5 h-full bg-gray-200 min-h-[16px]" />
                                  </div>
                                  {/* Milestone content */}
                                  <div className="flex-1 pb-2">
                                    <div className="flex items-center gap-2">
                                      <span className={cn(
                                        "text-sm",
                                        isPhotoDay && "font-semibold text-blue-700 dark:text-blue-400",
                                        isCompleted && !isPhotoDay && "text-muted-foreground line-through"
                                      )}>
                                        {ms.name}
                                      </span>
                                      {dueDateStr && (
                                        <span className={cn(
                                          "text-xs px-1.5 py-0.5 rounded",
                                          isPhotoDay ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" :
                                          ms.due_days < 0 ? "text-orange-600" : "text-muted-foreground"
                                        )}>
                                          {dueDateStr}
                                        </span>
                                      )}
                                      {isCompleted && (
                                        <CheckCircle className="w-3 h-3 text-green-500" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            };
                            
                            return (
                              <div key={wf.workflow_id} className="p-4 border rounded-lg">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <h4 className="font-medium">{wf.name}</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {(wf.workflow_type || 'other').replace('_', ' ')} • {wf.year}
                                    </p>
                                  </div>
                                  <Badge className={getWorkflowStatusStyle(wf.status)}>
                                    {(wf.status || 'pending').replace('_', ' ')}
                                  </Badge>
                                </div>
                                
                                {/* Progress bar */}
                                <div className="mb-4">
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-muted-foreground">Progress</span>
                                    <span>{calculateWorkflowProgress(wf)}%</span>
                                  </div>
                                  <Progress value={calculateWorkflowProgress(wf)} className="h-2" />
                                </div>
                                
                                {/* Milestone Timeline */}
                                {milestones.length > 0 && (
                                  <div className="space-y-0">
                                    {/* Before Photography Date - Collapsible if there are completed */}
                                    {beforePhotoMilestones.length > 0 && (
                                      <>
                                        {completedBeforeCount > 0 && !isExpanded ? (
                                          // Show collapsed summary
                                          <button
                                            className="flex items-start gap-3 w-full text-left hover:bg-muted/50 rounded p-1 -ml-1"
                                            onClick={() => setExpandedWorkflowMilestones(prev => ({
                                              ...prev,
                                              [wf.workflow_id]: true
                                            }))}
                                          >
                                            <div className="flex flex-col items-center">
                                              <div className="w-3 h-3 rounded-full bg-green-500 shrink-0 mt-0.5" />
                                              <div className="w-0.5 h-full bg-gray-200 min-h-[16px]" />
                                            </div>
                                            <div className="flex-1 pb-2">
                                              <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                <ChevronRight className="w-3 h-3" />
                                                {completedBeforeCount} completed milestone{completedBeforeCount > 1 ? 's' : ''}
                                                {pendingBeforeCount > 0 && (
                                                  <span className="text-orange-600">
                                                    ({pendingBeforeCount} pending)
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                          </button>
                                        ) : (
                                          // Show expanded milestones
                                          <>
                                            {completedBeforeCount > 0 && (
                                              <button
                                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2"
                                                onClick={() => setExpandedWorkflowMilestones(prev => ({
                                                  ...prev,
                                                  [wf.workflow_id]: false
                                                }))}
                                              >
                                                <ChevronDown className="w-3 h-3" />
                                                Collapse completed
                                              </button>
                                            )}
                                            {beforePhotoMilestones.map((ms, idx) => renderMilestone(ms, idx))}
                                          </>
                                        )}
                                      </>
                                    )}
                                    
                                    {/* Photography Date Milestone - Always visible */}
                                    {photoMilestone && renderMilestone(photoMilestone, 'photo', true)}
                                    
                                    {/* After Photography Date - Always visible */}
                                    {afterPhotoMilestones.map((ms, idx) => renderMilestone(ms, `after-${idx}`))}
                                    
                                    {/* If no photo milestone found, just show all */}
                                    {!photoMilestone && beforePhotoMilestones.length === 0 && afterPhotoMilestones.length === 0 && (
                                      milestones.map((ms, idx) => renderMilestone(ms, idx))
                                    )}
                                  </div>
                                )}
                                
                                <Button variant="link" size="sm" className="p-0 h-auto mt-2" onClick={() => navigate('/workflows', { state: { filterWorkflowId: wf.workflow_id } })}>
                                  View in Workflows <ArrowRight className="w-3 h-3 ml-1" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No workflows yet</p>
                          <Button variant="link" onClick={() => openQuickWorkflow(viewingOrg)}>
                            Create first workflow
                          </Button>
                        </div>
                      )}
                    </TabsContent>

                    {/* Communications Tab */}
                    <TabsContent value="communications" className="mt-4">
                      <CommunicationsTab
                        communications={communications}
                        communicationsLoading={communicationsLoading}
                        newCommType={newCommType}
                        setNewCommType={setNewCommType}
                        newCommNote={newCommNote}
                        setNewCommNote={setNewCommNote}
                        addingComm={addingComm}
                        addCommunicationNote={addCommunicationNote}
                        deleteCommunicationNote={deleteCommunicationNote}
                        openEmailDialog={openEmailDialog}
                        viewingOrg={viewingOrg}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        moveImportedNote={moveImportedNote}
                        allOrgs={organizations}
                      />
                    </TabsContent>

                    {/* Contacts Tab */}
                    <TabsContent value="contacts" className="mt-4">
                      <ContactsTab
                        contacts={viewingOrg.contacts}
                        openContactDialog={openAddContact}
                        openEditContactDialog={openEditContact}
                        setAsPrimary={handleSetPrimary}
                        handleDeleteContact={setDeleteContactId}
                        onSendEmailToContact={(contact) => {
                          setSelectedEmailContact(contact);
                          openEmailDialog(viewingOrg);
                        }}
                      />
                    </TabsContent>

                    {/* Logs Tab */}
                    <TabsContent value="logs" className="mt-6">
                      <LogsTab
                        orgLogs={orgLogs}
                        logsLoading={logsLoading}
                        logsPage={logsPage}
                        logsTotalPages={logsTotalPages}
                        fetchOrgLogs={fetchOrgLogs}
                        viewingOrgId={viewingOrg.org_id}
                        formatDate={formatDate}
                      />
                    </TabsContent>
                  </Tabs>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Unsaved Changes Dialog (replaces window.confirm for tab/year switch) */}
      <UnsavedChangesDialog
        open={unsavedDialog.open}
        onOpenChange={(v) => setUnsavedDialog((prev) => ({ ...prev, open: v }))}
        description={
          unsavedDialog.pendingAction?.kind === 'year'
            ? 'You have unsaved changes in the Sales form. What would you like to do before switching year?'
            : 'You have unsaved changes in the Sales form. What would you like to do before switching tab?'
        }
        saveLabel="Save & Switch"
        discardLabel="Discard & Switch"
        onSave={saveSalesData}
        onDiscard={() => {
          salesFormRef.current = {};
          setSalesDirty(false);
        }}
        onContinue={() => {
          const action = unsavedDialog.pendingAction;
          if (!action) return;
          if (action.kind === 'tab') {
            const tab = action.value;
            setActiveYearTab(tab);
            if (viewingOrg) {
              if (['sales', 'photography', 'production', 'accounts'].includes(tab)) {
                fetchSalesData(viewingOrg.org_id, salesYear);
              } else if (tab === 'orders') {
                fetchOrderData(viewingOrg.org_id, salesYear);
              }
            }
          } else if (action.kind === 'year') {
            const year = action.value;
            setSalesYear(year);
            setOrderYear(year);
            if (viewingOrg) {
              if (['sales', 'photography', 'production', 'accounts'].includes(activeYearTab)) {
                fetchSalesData(viewingOrg.org_id, year);
              } else if (activeYearTab === 'orders') {
                fetchOrderData(viewingOrg.org_id, year);
              }
            }
          }
          setUnsavedDialog({ open: false, pendingAction: null });
        }}
      />

      {/* Import Previous Year Sales Data — confirmation modal.
          Shows each importable field with a checkbox so the operator can
          opt-out per field, then asks for Merge vs Overwrite. Excluded
          fields (photography_date_1..5, date_booked,
          confirmation_letter_sent) are never shown here — the backend
          also enforces the exclusion. */}
      <Dialog open={importPreviewOpen} onOpenChange={setImportPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-previous-year-dialog">
          <DialogHeader>
            <DialogTitle>
              Import Sales Data from {importPreview?.source_year} → {importPreview?.target_year}
            </DialogTitle>
          </DialogHeader>
          {importPreview && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Copies non-year-specific sales fields from {importPreview.source_year} into {importPreview.target_year}.
                Fields tied to a specific shoot ({importPreview.excluded_fields?.filter(f => !['sales_id','org_id','year','created_at','updated_at'].includes(f)).join(', ')})
                are always skipped.
              </div>
              {importPreview.target_has_data && (
                <div className="p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
                  <strong>{importPreview.target_year} already has sales data.</strong>
                  Choose <em>Merge</em> to only fill blanks, or <em>Overwrite</em> to replace existing values with the {importPreview.source_year} data.
                </div>
              )}
              <div className="flex items-center gap-2 text-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next = {};
                    (importPreview.fields || []).forEach(f => { next[f.field] = true; });
                    setImportChecked(next);
                  }}
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setImportChecked({})}
                >
                  Select none
                </Button>
                <span className="text-muted-foreground ml-auto">
                  {Object.values(importChecked).filter(Boolean).length} of {(importPreview.fields || []).length} selected
                </span>
              </div>
              <div className="border rounded-md divide-y max-h-[45vh] overflow-y-auto">
                {(importPreview.fields || []).length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    {importPreview.source_year} has no importable field values.
                  </div>
                )}
                {(importPreview.fields || []).map(f => (
                  <label
                    key={f.field}
                    className="flex items-start gap-3 p-3 hover:bg-muted/40 cursor-pointer"
                    data-testid={`import-field-${f.field}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!importChecked[f.field]}
                      onChange={(e) => setImportChecked(prev => ({ ...prev, [f.field]: e.target.checked }))}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{f.label}</div>
                      <div className="text-xs text-muted-foreground truncate" title={String(f.value)}>
                        {String(f.value)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button
              variant="ghost"
              onClick={() => setImportPreviewOpen(false)}
              disabled={importBusy}
              data-testid="import-previous-year-cancel"
            >
              Cancel
            </Button>
            <div className="flex-1" />
            {importPreview?.target_has_data ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => runImport('merge')}
                  disabled={importBusy || !importPreview.fields?.length}
                  data-testid="import-previous-year-merge"
                >
                  {importBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                  Merge (fill blanks only)
                </Button>
                <Button
                  onClick={() => runImport('overwrite')}
                  disabled={importBusy || !importPreview.fields?.length}
                  data-testid="import-previous-year-overwrite"
                >
                  {importBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                  Overwrite existing
                </Button>
              </>
            ) : (
              <Button
                onClick={() => runImport('overwrite')}
                disabled={importBusy || !importPreview?.fields?.length}
                data-testid="import-previous-year-confirm"
              >
                {importBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                Import
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Organization Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrg ? 'Edit Organization' : 'Add Organization'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="md:col-span-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  data-testid="org-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="sector_id">Sector *</Label>
                <Select 
                  value={formData.sector_id} 
                  onValueChange={(v) => setFormData({...formData, sector_id: v})}
                >
                  <SelectTrigger data-testid="org-sector-select">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map(s => (
                      <SelectItem key={s.sector_id} value={s.sector_id}>{s.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="region">Region</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  data-testid="org-region-input"
                />
              </div>
              <div>
                <Label htmlFor="postcode">Postcode</Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://..."
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  data-testid="org-website-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-org-btn">{editingOrg ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Workflow Dialog */}
      <Dialog open={quickWorkflowDialogOpen} onOpenChange={setQuickWorkflowDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start Workflow for {selectedOrg?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickWorkflow}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Workflow Name *</Label>
                <Input
                  value={workflowData.name}
                  onChange={(e) => setWorkflowData({...workflowData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select 
                  value={workflowData.category} 
                  onValueChange={(v) => setWorkflowData({...workflowData, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="prephotoday">Schedule</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="feedback">Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Photography Date</Label>
                <Input
                  type="date"
                  value={workflowData.photography_date}
                  onChange={(e) => setWorkflowData({...workflowData, photography_date: e.target.value})}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-creates: Confirmation Email (-42), Photography Date (0), Dispatch Date (+42)
                </p>
              </div>
              {workflowTemplates.length > 0 && (
                <div>
                  <Label>Use Template (Optional)</Label>
                  <Select 
                    value={workflowData.template_id || 'none'} 
                    onValueChange={(v) => setWorkflowData({...workflowData, template_id: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {workflowTemplates.filter(t => t.template_id).map(t => (
                        <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuickWorkflowDialogOpen(false)}>Cancel</Button>
              <Button type="submit">
                <FolderPlus className="w-4 h-4 mr-2" />
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Workflow Confirmation Email Prompt */}
      <Dialog open={showWorkflowEmailPrompt} onOpenChange={setShowWorkflowEmailPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Confirmation Email?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Would you like to send a confirmation email to <strong>{selectedOrg?.name}</strong> after creating this workflow?
            </p>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Mail className="w-5 h-5 text-primary" />
              <div className="text-sm">
                <p className="font-medium">Confirmation emails help ensure:</p>
                <ul className="text-muted-foreground text-xs mt-1 list-disc list-inside">
                  <li>Organisation is aware of the scheduled photography</li>
                  <li>Key details are confirmed in writing</li>
                </ul>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={createWorkflowOnly}
              data-testid="workflow-no-email-btn"
            >
              No, Just Create Workflow
            </Button>
            <Button 
              onClick={createWorkflowWithEmail}
              data-testid="workflow-with-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Yes, Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Confirmation Email Template Selection */}
      <Dialog open={workflowEmailDialogOpen} onOpenChange={setWorkflowEmailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Choose an email template to send to <strong>{pendingWorkflowOrg?.name}</strong>:
            </p>
            
            {/* Compose New Email Option */}
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
              onClick={() => {
                setWorkflowEmailDialogOpen(false);
                navigate(`/email?org_id=${pendingWorkflowOrg?.org_id}`);
              }}
              data-testid="compose-new-workflow-email"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <PenLine className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Compose New Email</h4>
                  <p className="text-sm text-muted-foreground">
                    Start with a blank email pre-filled with contact details
                  </p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Templates List */}
            <div>
              <h4 className="font-medium mb-3">Or use a template:</h4>
              {emailTemplatesLoading ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : emailTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No templates available. Create templates in Email Hub.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.template_id}
                      className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                      onClick={() => {
                        setWorkflowEmailDialogOpen(false);
                        navigate(`/email?org_id=${pendingWorkflowOrg?.org_id}&template_id=${template.template_id}`);
                      }}
                      data-testid={`workflow-template-${template.template_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-muted rounded">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium truncate">{template.name}</h5>
                          <p className="text-xs text-muted-foreground truncate">
                            {template.subject || 'No subject'}
                          </p>
                        </div>
                        {template.category && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {template.category.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkflowEmailDialogOpen(false)}>
              Skip Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sector Management Dialog */}
      <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Sectors</DialogTitle>
          </DialogHeader>
          
          {/* List existing sectors */}
          <div className="space-y-2 mb-4">
            {sectors.map(s => (
              <div key={s.sector_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Badge className={getSectorColor(s.sector_id)}>{s.display_name}</Badge>
                  <span className="text-xs text-muted-foreground">({s.name})</span>
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-background border">
                    {sectorOrgCounts[s.sector_id] || 0} orgs
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setEditingSector(s);
                      setSectorForm({ name: s.name, display_name: s.display_name, color: s.color || 'blue' });
                    }}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleDeleteSector(s.sector_id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
            {sectors.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No sectors yet</p>
            )}
          </div>

          <Separator />

          {/* Add/Edit Sector Form */}
          <form onSubmit={handleSaveSector} className="space-y-4">
            <h4 className="font-medium">{editingSector ? 'Edit Sector' : 'Add New Sector'}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sector Code *</Label>
                <Input
                  value={sectorForm.name}
                  onChange={(e) => setSectorForm({...sectorForm, name: e.target.value})}
                  placeholder="e.g. school"
                  required
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={sectorForm.display_name}
                  onChange={(e) => setSectorForm({...sectorForm, display_name: e.target.value})}
                  placeholder="e.g. School"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>Color</Label>
                <Select value={sectorForm.color} onValueChange={(v) => setSectorForm({...sectorForm, color: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sectorColors.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {editingSector && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setEditingSector(null); setSectorForm({ name: '', display_name: '', color: 'blue' }); }}
                >
                  Cancel Edit
                </Button>
              )}
              <Button type="submit" disabled={sectorLoading}>
                {sectorLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {editingSector ? 'Update' : 'Add'} Sector
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Template Selection Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={(open) => {
        setEmailDialogOpen(open);
        if (!open) setSelectedEmailContact(null);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Email to {selectedEmailContact
                ? `${selectedEmailContact.name || 'Contact'} (${selectedEmailContact.email || 'no email'})`
                : selectedEmailOrg?.name}
            </DialogTitle>
            {selectedEmailContact && selectedEmailOrg && (
              <p className="text-xs text-muted-foreground">
                Organisation: {selectedEmailOrg.name}
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Choose to use a template with auto-filled variables or compose a new email.
            </p>
            
            {/* Compose New Option */}
            <div 
              className="p-4 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
              onClick={() => navigateToEmailHub(selectedEmailOrg)}
              data-testid="compose-new-email-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <PenLine className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Compose New Email</h4>
                  <p className="text-sm text-muted-foreground">
                    Start with a blank email pre-filled with contact details
                  </p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Templates List */}
            <div>
              <h4 className="font-medium mb-3">Or use a template:</h4>
              {emailTemplatesLoading ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : emailTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No templates available. Create templates in Email Hub.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.template_id}
                      className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                      onClick={() => navigateToEmailHub(selectedEmailOrg, template)}
                      data-testid={`template-${template.template_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-muted rounded">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium truncate">{template.name}</h5>
                          <p className="text-xs text-muted-foreground truncate">
                            {template.subject || 'No subject'}
                          </p>
                        </div>
                        {template.category && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {template.category.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              <strong>Tip:</strong> Templates support variables: <code className="bg-muted px-1 rounded">{'{{Org_name}}'}</code> and <code className="bg-muted px-1 rounded">{'{{Pri_contact}}'}</code>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Edit/Add Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  required
                  placeholder="Contact name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  required
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  value={contactForm.mobile}
                  onChange={(e) => setContactForm({...contactForm, mobile: e.target.value})}
                  placeholder="Mobile number"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Input
                  value={contactForm.role}
                  onChange={(e) => setContactForm({...contactForm, role: e.target.value})}
                  placeholder="e.g., Principal, Manager"
                />
              </div>
              <div>
                <Label>Notes | Address</Label>
                <Textarea
                  value={contactForm.notes_address}
                  onChange={(e) => setContactForm({...contactForm, notes_address: e.target.value})}
                  placeholder="Notes and/or address for this contact"
                  rows={3}
                  data-testid="contact-notes-address"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm({...contactForm, is_primary: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_primary" className="cursor-pointer">
                  Set as primary contact
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={contactLoading}>
                {contactLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingContact ? 'Update' : 'Add'} Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Contact Confirmation */}
      <Dialog open={!!deleteContactId} onOpenChange={() => setDeleteContactId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Contact?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            Are you sure you want to delete this contact? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteContactId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteContact}>
              Delete Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Online Orders Dialog */}
      <Dialog open={onlineOrdersOpen} onOpenChange={setOnlineOrdersOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-500" />
              Online Orders - {viewingOrg?.name}
            </DialogTitle>
            <DialogDescription>
              WooCommerce orders from the last 100 days matching this organisation
            </DialogDescription>
          </DialogHeader>
          
          {onlineOrdersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{onlineOrdersSummary.total}</p>
                  <p className="text-sm text-blue-800 dark:text-blue-300">Total Orders</p>
                </div>
                {isAdmin && (
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">${onlineOrdersSummary.revenue?.toLocaleString()}</p>
                    <p className="text-sm text-emerald-800 dark:text-emerald-300">Total Revenue</p>
                  </div>
                )}
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{onlineOrdersSummary.familyOrders}</p>
                  <p className="text-sm text-purple-800 dark:text-purple-300">Family Orders</p>
                </div>
              </div>

              {/* Orders List */}
              {onlineOrders.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Status</TableHead>
                        {isAdmin && <TableHead className="text-right">Total</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {onlineOrders.map((order) => (
                        <TableRow key={order.order_number} className={cn(order.has_family && "bg-purple-50/50 dark:bg-purple-950/20")}>
                          <TableCell className="font-medium">
                            #{order.order_number}
                            {order.has_family && (
                              <Badge variant="outline" className="ml-2 text-[10px] bg-purple-100 text-purple-700 border-purple-300">
                                Family
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{order.date_created}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{order.customer_name || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[150px]">{order.customer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1 max-w-[250px]">
                              {order.items?.slice(0, 2).map((item, idx) => (
                                <p key={idx} className="text-xs truncate" title={item.name}>
                                  {item.quantity}x {item.name}
                                </p>
                              ))}
                              {order.items?.length > 2 && (
                                <p className="text-xs text-muted-foreground">+{order.items.length - 2} more</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              order.status === 'completed' ? 'default' :
                              order.status === 'processing' ? 'secondary' :
                              order.status === 'on-hold' ? 'outline' : 'destructive'
                            } className="text-xs capitalize">
                              {order.status?.replace('-', ' ')}
                            </Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-right font-medium">
                              ${parseFloat(order.total || 0).toLocaleString()}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg bg-muted/20">
                  <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No orders found matching this organisation</p>
                  <p className="text-xs text-muted-foreground mt-1">Try checking if the organisation name matches the product names in WooCommerce</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOnlineOrdersOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
